
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// import Button from 'react-bootstrap/Button';

import { Button, ColorPicker, Input, Segmented, Select, Slider, Space, InputNumber, Rate, Skeleton, Pagination, Popover, Modal, Dropdown, Tooltip, List, Empty, Spin, Switch, message } from 'antd';
import DateField from './common/DateField';
import FieldHelp from './common/FieldHelp';
import ToggleRow from './common/ToggleRow';
import RecordBadge, { RecordIconTile } from './common/RecordBadge';
import ColorInputPanel from './common/ColorInputPanel';
import IconColorPicker, { iconLabelOf } from './common/IconColorPicker';
import { location as geolocation } from '../platform';
import ScanButton from './common/ScanButton';
import FileUploadField from './common/FileUploadField';
import { COLOR_PRESETS, swatchColorOf } from './common/ColorSwatch';
import { normalizeColor } from '../utils/elementFormat/color';
import { AUTOFILL_OPT_OUT_ATTRS, NO_TEXT_ASSIST } from '../utils/fieldAssist';
import { buildActionMenu } from './common/actionMenu';

import { api } from '../api/client';
import UserPickerSelect from './users/UserPickerSelect';
import { normalizeUserValue } from '../utils/userElements';
import { ratingScale } from '../utils/rating';
import dayjs from 'dayjs';
import { formatDisplayDate, formatDisplayDateTime, formatDisplayTime } from '../utils/dateDisplay';
import useDateFormat from '../hooks/useDateFormat';

import { API_ITEMS_URL, API_COLLECTIONS_URL } from '../utils';
import { FONT_COLOR_DARK } from '../utils/colors';
import { CLOSE, EYE, EYE_SLASH, GEO_PIN, KEBAB, MINUS, PLUS } from './icons';
import LibraryIcon from './common/LibraryIcon';
import { isLibraryIconName } from '../utils/libraryIcons';
import { buildItemDisplayValue } from '../utils/displayTemplate';
import { buildChoiceOptions } from './common/ChoiceOptions';
import { canonicalChoices, choiceEntries, choiceListOf } from '../utils/choiceValues';
import { createLookupResolver, LOOKUP_DATA_TYPE } from '../utils/lookups';
import { applyStrip, resolveScan, scanConfig } from '../services/scanLookup';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { cardChain, mergeChainElements } from '../utils/cards';
import useLinkedItems from '../hooks/useLinkedItems';
import useEnsureCards from '../hooks/useEnsureCards';
import useMe from '../hooks/useMe';
import { lookupFamilyHasIdentity, lookupHoldsMe, withMe } from '../utils/lookupMe';
import { isStrictLookup, lookupFilterOf, lookupTargetCardIds } from '../utils/lookupFilter';
import { isOffered } from '../services/lookupOffers';
import useLookupSearch, { lookupTotalOf } from '../hooks/useLookupSearch';
import { lookupSearchFields } from '../utils/lookupSearch';
import { buildFilterFields, tagsFilterField } from '../utils/kqlBuilder';
import useCollectionTags from '../hooks/useCollectionTags';
import { summarizeQuery } from '../utils/filterSummary';
import CopyReferencesModal from './globalCollections/CopyReferencesModal';
import LookupUnavailable from './common/LookupUnavailable';
import useLinkedSources from '../hooks/useLinkedSources';
import useSelectedLookupRows from '../hooks/useSelectedLookupRows';
import { NOT_READABLE_LABEL, itemHomeCollectionId, markReadable, useNotReadableIds } from '../utils/linkedRecords';
import { canManage as canManageCollection } from '../utils/permissions';
import { familyIdsOf, referencedRecordPath } from '../utils/collectionFamily';
import { useCollectionContext } from '../contexts/CollectionContext';
import { collectionZone } from '../utils/collectionTimezone';
import { booleanLabels, formatBooleanValue, getElementInitialValue, getMissingRequiredElements, getNumericInputMode, numberInputConstraints, stepNumberValue } from '../utils/elements';
import { formatMeasurement, getMeasure, getUnit, isMeasurementValue, joinComposite, splitComposite } from '../utils/units';
import { formatNumber, fractionsOf, percentOf, thousandsOf } from '../utils/elementFormat/number';
import { fractionEntryProblem, numberEntryProps } from '../utils/numberEntry';
// The shared format modules, vendored byte-identical from keepr-api: the form
// parses a phone number and a location with the very code the API validates
// them with, so an input that accepts a value cannot produce a 400.
import { DEFAULT_COUNTRY, formatPhone, normalizeCountry, parsePhone } from '../utils/elementFormat/phone';
import { LAT_LIMIT, LNG_LIMIT, parseLocation } from '../utils/elementFormat/location';
import { acceptsUnit, allowedUnitsFor, compositeParts, defaultUnitFor, groupUnitOptions, readEntryValue, readLastUsedUnit, unitSymbol, writeLastUsedUnit } from '../utils/measurements';
import { exponentOf, formatCurrency, isCurrencyValue, toMinor, majorText } from '../utils/currency';
import { CURRENCY_SEARCH_THRESHOLD, amountTextOf, clampDecimals, currencyOption, currencySearchFilter, defaultCurrencyFor, entryCurrencies, entryFromText, groupAmountText, readEntryCurrency } from '../utils/money';
import { useNavigate } from 'react-router-dom';
import { foldSearchText } from '../utils/strings';
import { optionSearchFilter } from '../utils/optionSearchFilter';
import { isRepeatingGroup } from '../config/repeatingGroups';
import { MASK_TEXT, isMaskedElement } from '../utils/maskedElements';
import { entryCountWords } from '../utils/repeatingGroupEntries';
import useWindowClass from '../hooks/useWindowClass';

const { TextArea } = Input;

// TipTap only loads when a rich-text element is actually rendered.
const RichTextEditor = lazy( () => import( './common/RichTextEditor' ) );

const normalizeDbId = maybe => {
	if (!maybe) return undefined;
	if (typeof maybe === 'string') return maybe;
	if (typeof maybe === 'object') {
		if (typeof maybe.$oid === 'string') return maybe.$oid;
		if (maybe._id) return normalizeDbId(maybe._id);
		if (maybe.id) return normalizeDbId(maybe.id);
		try { return maybe.toString(); } catch { return undefined; }
	}
	return undefined;
};

/**
 * `elementDefs` and `resolveLookupValue` are what let a target card whose own
 * display template nests a card-lookup render that token as a label instead of
 * a raw id. Without them buildItemDisplayValue has no way to tell a lookup
 * element from any other string.
 */
const getItemDisplayLabel = (item, { template, elementDefs, resolveLookupValue } = {}) => {
	if (!item) return '';
	const id = normalizeDbId(item?._id);
	const label = buildItemDisplayValue({
		template,
		elements: item?.elements || {},
		elementDefinitions: elementDefs,
		resolveLookupValue,
		name: item?.name,
		id,
	});
	return label || id || '';
};

const MAX_LOOKUP_INLINE_OPTIONS = 10;
// A typed search asks for as many rows as the first page holds, so the next
// keystroke has a full page to narrow in the browser while its own request
// is in flight.
const LOOKUP_SEARCH_PAGE_SIZE = 50;
const VIEW_ALL_OPTION_VALUE = '__card_lookup_view_all__';
const VIEW_ALL_PAGE_SIZE = 12;

const getCardLabel = (card, { plural = false } = {}) => {
	if (!card) return plural ? 'items' : 'item';
	const fromLabel = plural ? card?.label?.plural : card?.label?.singular;
	if (fromLabel) return fromLabel;
	if (card?.name) return plural ? `${card.name}s` : card.name;
	return plural ? 'items' : 'item';
};

// Exported because every "preview one record's fields" surface has to format
// values identically — the lookup detail modal here and the driven-source modal
// (components/items/DrivenSourceModal.js) render the same card-lookup-detail
// markup and must not drift apart on dates, arrays, or nested lookups.
export const formatLookupElementValue = (element, value, resolveLookupValue) => {
	if (value === undefined || value === null || value === '') return '—';
	// A masked element (KPR-1348) previews as its mask: the record itself,
	// one click away, has Show and Copy.
	if (isMaskedElement(element)) return MASK_TEXT;
	const dataType = element?.dataType;
	// A repeating group's value is a list of entries ({ id, elements }) and
	// section headings ({ id, section }), never one value: it says how many
	// entries it holds (KPR-1108). The array and object branches below would
	// print each one as JSON.
	if (isRepeatingGroup(element)) return entryCountWords(value) || '—';
	// A lookup pointing at another lookup: the resolver already joins multiples.
	if (dataType === LOOKUP_DATA_TYPE && resolveLookupValue) {
		return resolveLookupValue(value) || '—';
	}
	if (Array.isArray(value)) {
		return value
			.map(entry => formatLookupElementValue({ ...element, dataType }, entry, resolveLookupValue))
			.filter(part => part !== '—')
			.join(', ') || '—';
	}
	if (dataType === 'date' || dataType === 'date-time') {
		const parsed = dayjs(value);
		if (parsed.isValid()) {
			return dataType === 'date' ? formatDisplayDate(value) : formatDisplayDateTime(value);
		}
	}
	// 'HH:mm' is not a shape dayjs reads, so a time goes through the stored
	// time reader (which also takes the legacy ISO timestamps).
	if (dataType === 'time') {
		return formatDisplayTime(value) || '—';
	}
	// A measurement is a stored object; the element's display rules render it.
	if (dataType === 'measurement') {
		return formatMeasurement(value, element?.options || {}) || '—';
	}
	// So is money: "$1,234.50", in the element's own style.
	if (dataType === 'currency') {
		return formatCurrency(value, element?.options || {}) || '—';
	}
	if (typeof value === 'object') {
		if (value === null) return '—';
		if (typeof value.name === 'string') return value.name;
		if (typeof value.label === 'string') return value.label;
		const stringified = (() => {
			try { return JSON.stringify(value); } catch { return null; }
		})();
		return stringified || '—';
	}
	if (typeof value === 'boolean') return formatBooleanValue(element, value);
	return String(value);
};



//////////////////////////////////////////////////
//   
//   WRAPPER
//   
//////////////////////////////////////////////////

// `help` is the element's author-written help text; FieldHelp turns it into
// the label's hover hint and a "?" popover, and renders nothing extra without it.
const Wrapper = ( { children, elementLabel, elementName, labelInline = false, showSkeleton = false, labelBadge = null, required = false, help = '' } ) => {
	
	//


	return <>
		<div 
			className={'' + ( labelInline ? 'd-flex' : 'mb-2' )}
			style={{
				// border: '1px solid ' + COLOR_LIGHT_GRAY,
				// backgroundColor: COLOR_LIGHT_GRAY,
			}}
		>
			<Skeleton loading={showSkeleton} active>
				<FieldHelp text={help} label={elementLabel}>
					<label
						htmlFor={elementName}
						className={labelInline ? 'my-auto' : 'd-block' }
						style={{ minWidth: 'fit-content', height: 'fit-content' }}
					>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
							{labelBadge ? (
								<span style={{ display: 'inline-flex', alignItems: 'center' }}>
									{labelBadge}
								</span>
							) : null}
							<span>
								{elementLabel}
								{required ? <span className='required-indicator' aria-hidden='true'>*</span> : null}
							</span>
							{labelInline ? <>&nbsp;</> : null}
						</span>
					</label>
				</FieldHelp>
				{children}
			</Skeleton>
		</div>
	</>;


	// return <>
	// 	<div className={'' + ( labelInline ? 'd-flex' : 'mb-2' )}>
	// 		<label 
	// 			htmlFor={'input-' + elementName}
	// 			className={labelInline ? 'my-auto' : 'd-block' } 
	// 			// style={labelStyle}
	// 			style={{ minWidth: 'fit-content', height: 'fit-content' }}
	// 		>
	// 			{elementLabel}
	// 			{labelInline ? <>&nbsp;</> : <></>}
	// 		</label>
	// 		{children}
	// 	</div>
	// </>;

}



//////////////////////////////////////////////////
//   
//   TEXT
//   
//////////////////////////////////////////////////

// The vendor opt-out markers live in utils/fieldAssist.js, beside the page-wide
// guard that applies them to every field that does not ask for autofill. Element
// inputs still spread them here, so a field carries them from its first render.

// Element fields are app data, not the visitor's own details, so autofill is
// OFF by default everywhere: an item element literally named 'name' or
// 'address' otherwise invites Chrome/Safari — or a password manager's popup —
// to offer the signed-in person's own name or home address for a field about
// a car or a client.
//
// Three levels, most specific first:
//  - an explicit `autoComplete` token from the caller (login, profile) wins —
//    those forms DO want the help and name the field's role;
//  - `allowAutocomplete` opts a single element back in ('on', browser
//    heuristics), which is what the card definition's "Allow browser autofill"
//    switch sets for a field that really does hold a person's details;
//  - otherwise 'off', plus the vendor markers above.
//
// Returns a prop bundle to spread onto the input, because opting out takes
// more than one attribute. An explicit 'off' from a caller gets the vendor
// markers too — asking for off means off, whoever is doing the filling.
export function getAutofillProps ( autoComplete, allowAutocomplete ) {
	const resolved = autoComplete || ( allowAutocomplete ? 'on' : 'off' );
	return resolved === 'off'
		? { autoComplete: 'off', ...AUTOFILL_OPT_OUT_ATTRS }
		: { autoComplete: resolved };
}

//////////////////////////////////////////////////
//
//   MASKED ENTRY (KPR-1348)
//
//////////////////////////////////////////////////

/**
 * A masked element's input (utils/maskedElements.js): what is typed is drawn
 * as discs until **Show** is pressed, and Show hides it again. The field
 * stays a TEXT field with the browser's text-security style
 * (css/components/masked-value.css), never `type="password"`: a password
 * field wakes the password manager, and a door code is not the person's
 * password (docs/platform-standards.md § 7, the field-assist ruling). It also
 * asks for no autocorrect, spelling or autofill (NO_TEXT_ASSIST plus the
 * password managers' own opt-outs). The toggle is the input's action affix:
 * a Space.Compact button on the right, with `aria-pressed`, labelled for a
 * screen reader. Its state is the form's own and is never saved.
 */
function useMaskedEntry ( masked, elementLabel ) {
	const [ shown, setShown ] = useState( false );
	if ( ! masked ) return { hidden: false, inputProps: {}, toggle: null };
	const name = typeof elementLabel === 'string' && elementLabel ? elementLabel : 'value';
	const hidden = ! shown;
	return {
		hidden,
		inputProps: {
			...NO_TEXT_ASSIST,
			...AUTOFILL_OPT_OUT_ATTRS,
			'data-masked': hidden ? 'hidden' : 'shown',
		},
		className: hidden ? 'masked-input--hidden' : '',
		toggle: (
			<Tooltip title={ shown ? 'Hide' : 'Show' }>
				<Button
					size='large'
					className='masked-input__toggle'
					icon={ shown ? <EYE_SLASH /> : <EYE /> }
					aria-label={ `${ shown ? 'Hide' : 'Show' } ${ name }` }
					aria-pressed={ shown }
					onClick={ () => setShown( on => ! on ) }
				/>
			</Tooltip>
		),
	};
}

export function InputText ( props ) {
	
	const { 
		elementName, 
		// elementLabel, 
		value, 
		onChange, 
		disabled = false, 
		required = false,
		placeholder = null,
		autoCapitalize = 'sentences', /* none|words|characters */
		autoComplete,
		allowAutocomplete = false,
		disableAutocorrect = false,
		inputMode,
		// `options.scan` — a camera button as the RIGHTMOST member of a
		// Space.Compact group, the house affix rule for an action
		// (docs/platform-standards.md). Absent, the input is exactly what it
		// was: one bare Input, no wrapper group, no layout change.
		scan = null,
		// Inline feedback (EventFormElement): the field's error state, and
		// the check that runs when it is left — a url's normalisation and the
		// text ceilings (utils/elementValidation).
		status,
		onBlur,
		// antd's character count ({ show, max, strategy }) — shown when the
		// element's author set a maximum length (docs/plans/data-types-
		// second-pass.md §3.4). Absent, no count.
		count,
		// A masked element (KPR-1348): typing is hidden, with a Show toggle.
		masked = false,
		elementLabel,
		// showSkeleton,
	} = props;

	const handleChange = e => onChange( e.target.name, e.target.value );
	const mask = useMaskedEntry( masked, elementLabel );
	const affixed = scan || mask.toggle;

	const field = (
		<Input
			type='text'
			name={elementName}
			id={elementName}
			// id={'input-' + elementName}
			// addonBefore={elementLabel}
			size='large'
			className={[ affixed ? 'input-affix-group__input' : '', mask.className || '' ].filter( Boolean ).join( ' ' )}
			placeholder={placeholder}
			autoCapitalize={autoCapitalize}
			{...getAutofillProps( autoComplete, allowAutocomplete )}
			autoCorrect={disableAutocorrect ? 'off' : undefined}
			inputMode={inputMode}
			disabled={disabled}
			required={required}
			status={status}
			onBlur={onBlur}
			count={count}
			{...mask.inputProps}
			value={value} 
			onChange={handleChange} />
	);

	return <>
		<Wrapper
			{...props}
			// elementName={elementName}
			// elementLabel={elementLabel}
			// showSkeleton={showSkeleton}
		>
			{ affixed ? (
				<Space.Compact className='input-affix-group' block>
					{field}
					{mask.toggle}
					{ scan ? <ScanButton
						formats={scan.formats}
						disabled={disabled}
						// The decoded string is the value, minus whatever fixed
						// affix the printer adds and the field does not want.
						onDecode={decoded => onChange( elementName, applyStrip( decoded, scan.strip ) )}
					/> : null }
				</Space.Compact>
			) : field }
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//   
//   PASSWORD
//   
//////////////////////////////////////////////////

export function InputPassword ( { elementName, elementLabel, value, onChange, 
	disabled = false, 
	required = false,
	placeholder = null,
	autoComplete,
	labelBadge,
 } ) {
		
		const handleChange = e => onChange( e.target.name, e.target.value );
		
		return <>
		<Wrapper 
			elementName={elementName} 
			elementLabel={elementLabel}
			labelBadge={labelBadge}>
			<Input.Password
				type='text'
				name={elementName}
				id={elementName}
				// id={'input-' + elementName}
				size='large'
				className=''
				placeholder={placeholder}
				autoComplete={autoComplete}
				disabled={disabled}
				required={required}
				value={value} 
				onChange={handleChange} />
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//   
//   TEXTAREA
//   
//////////////////////////////////////////////////

export function InputTextArea ( props ) {
	
	const { 
		elementName, 
		// elementLabel, 
		value, 
		onChange, 
		disabled = false, 
		required = false,
		autoCapitalize = 'sentences', /* none|words|characters */ 
		placeholder = null, 
		autoComplete,
		allowAutocomplete = false,
		// A field whose NAME is already the panel it sits in (the item form's
		// Notes) carries no visible label, so it says its name to the
		// accessibility tree instead.
		ariaLabel = undefined,
		maxLength = undefined,
		// Inline feedback (EventFormElement): the error state, and the
		// ceiling check that runs when the field is left.
		status,
		onBlur,
		// antd's character count, when the author set a maximum length.
		count,
		// A masked element (KPR-1348): typing is hidden, with a Show toggle.
		masked = false,
		elementLabel,
	} = props;

	const handleChange = e => onChange( e.target.name, e.target.value );
	const mask = useMaskedEntry( masked, elementLabel );
	


	return <>
		<Wrapper 
			{...props}
			// elementName={elementName} 
			// elementLabel={elementLabel}
		>
			{ ( () => {
				const area = (
					<TextArea 
						name={elementName}
						id={elementName}
						// id={'input-' + elementName}
						size='large'
						className={mask.className || undefined}
						placeholder={placeholder}
						autoCapitalize={autoCapitalize}
						aria-label={ariaLabel}
						maxLength={maxLength}
						status={status}
						onBlur={onBlur}
						count={count}
						{...getAutofillProps( autoComplete, allowAutocomplete )}
						{...mask.inputProps}
						disabled={disabled}
						required={required}
						value={value}
						onChange={handleChange} 
						autoSize={{ minRows: 2, maxRows: 8, }}
					/>
				);
				// A masked long text (KPR-1348): its Show toggle sits on the
				// right of the box, as an input's action affix does.
				return mask.toggle ? (
					<div className='masked-input__row'>
						{area}
						{mask.toggle}
					</div>
				) : area;
			} )() }
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//
//   RICH TEXT
//
//////////////////////////////////////////////////

export function InputRichText ( props ) {

	const {
		elementName,
		value,
		onChange,
		disabled = false,
	} = props;

	const handleChange = markdown => onChange( elementName, markdown );

	return <>
		<Wrapper {...props}>
			<Suspense fallback={<Skeleton.Input active block size='large' />}>
				<RichTextEditor
					id={elementName}
					value={typeof value === 'string' ? value : ''}
					onChange={handleChange}
					disabled={disabled}
				/>
			</Suspense>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//
//   NUMBER
//
//////////////////////////////////////////////////

export function InputNum ( { elementName, elementLabel, help = '', value, onChange,
	disabled = false, labelBadge = null, required = false, incrementButtons = false,
	dataType = 'number', options = null, status = undefined, onBlur = undefined, masked = false } ) {

	// A masked element (KPR-1348): typing is hidden, with a Show toggle — and
	// never a slider, whose handle would say the value.
	const mask = useMaskedEntry( masked, elementLabel );

	// The element's number options as antd reads them: fixed decimals, and
	// the bounds the API enforces on write (docs/plans/data-types.md
	// decision 3). antd clamps a TYPED value to min/max on its own; the
	// steppers below are ours, so they clamp themselves.
	const { precision, min, max, step } = numberInputConstraints( dataType, options );
	// PR 7's number polish (docs/plans/data-types-second-pass.md §3.8):
	//  - percent: a passive '%' suffix inside the input (the affix rule:
	//    passive display inside, actions joined on the right). The value is
	//    the number typed — 12.5 is 12.5 % (ruling D9).
	//  - thousands: the grouping shows whenever the field is not being typed
	//    in, so the typed value always wins; commas typed or pasted are read
	//    past. (antd's own formatter / parser pair, through the shared
	//    formatter so the field and the list group the same digits.)
	//  - step: what − / +, antd's arrows and the slider move by.
	//  - control 'slider': antd's Slider between the two bounds, the number
	//    input beside it — the slider for a quick pick, the input for the
	//    exact value (and for a keyboard). A slider whose bounds are gone
	//    (refused by the API, so only legacy data) falls back to the input.
	const percent = percentOf( options );
	const thousands = thousandsOf( options );
	// KPR-1025: every number field reads a typed fraction ("1 1/2", "3/4",
	// "½") as the number it is, and "Show as a fraction" shows the value as
	// one when the field is not being typed in (src/utils/numberEntry.js).
	// An integer never shows one: it has nothing after the point.
	const fractions = dataType !== 'integer' && fractionsOf( options );
	const slider = ! masked && options?.control === 'slider' && min !== undefined && max !== undefined && min < max;
	const entryProps = numberEntryProps( { fractions, thousands, precision, value } );
	// The sentence for a malformed fraction, shown once the field is left (a
	// half-typed "1 1/" is not a mistake yet) and cleared by the next edit.
	const typedRef = useRef( '' );
	const [ fractionProblem, setFractionProblem ] = useState( null );

	const clamp = next => {
		if ( typeof next !== 'number' || ! Number.isFinite( next ) ) return next;
		if ( min !== undefined && next < min ) return min;
		if ( max !== undefined && next > max ) return max;
		return next;
	};

	const handleChange = value => {
		if ( fractionProblem ) setFractionProblem( null );
		onChange( elementName, value );
	};

	const handleStep = direction => {
		typedRef.current = '';
		onChange( elementName, clamp( stepNumberValue( value, direction, step ) ) );
	};

	const handleBlur = event => {
		setFractionProblem( fractionEntryProblem( typedRef.current ) );
		if ( onBlur ) onBlur( event );
	};

	// antd's InputNumber renders a type='text' input (it owns parsing, so it
	// can't use type='number'), which means a phone shows the full QWERTY
	// keyboard unless we ask for the keypad. inputMode rides through
	// antd -> rc-input-number -> the DOM input untouched.
	const numberInput = <InputNumber
		name={elementName}
		id={elementName}
		// id={'input-' + elementName}
		size='large'
		className={[ incrementButtons || mask.toggle ? 'input-affix-group__input' : 'w-100', mask.className || '' ].filter( Boolean ).join( ' ' )}
		disabled={disabled}
		value={value}
		onChange={handleChange}
		onBlur={handleBlur}
		onInput={text => { typedRef.current = text; }}
		status={fractionProblem ? 'error' : status}
		precision={precision}
		min={min}
		max={max}
		step={step}
		suffix={percent ? '%' : undefined}
		{...entryProps}
		controls={! incrementButtons}
		// A phone's number pad has no slash or space: an element that shows
		// fractions asks for the full keyboard, so one can be typed.
		inputMode={fractions ? 'text' : getNumericInputMode( dataType )}
		{...getAutofillProps()}
		{...mask.inputProps}
		/>;

	const entry = incrementButtons ? (
		<Space.Compact className='input-affix-group input-affix-group--stack' block>
			{numberInput}
			{mask.toggle}
			<Button
				size='large'
				icon={<MINUS />}
				disabled={disabled}
				onClick={() => handleStep( -1 )}
				aria-label={`Decrease ${elementLabel}`}
			/>
			<Button
				size='large'
				icon={<PLUS />}
				disabled={disabled}
				onClick={() => handleStep( 1 )}
				aria-label={`Increase ${elementLabel}`}
			/>
		</Space.Compact>
	) : mask.toggle ? (
		<Space.Compact className='input-affix-group' block>
			{numberInput}
			{mask.toggle}
		</Space.Compact>
	) : numberInput;

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			{slider ? (
				<div className='input-slider'>
					<Slider
						className='input-slider__track'
						min={min}
						max={max}
						step={step}
						// A blank value rests the handle at the minimum; nothing
						// is stored until it is moved or a number is typed.
						value={typeof value === 'number' && Number.isFinite( value ) ? value : min}
						onChange={next => onChange( elementName, next )}
						disabled={disabled}
						tooltip={{ formatter: next => formatNumber( next, options || {} ) }}
						ariaLabelForHandle={elementLabel}
					/>
					<div className='input-slider__value'>{entry}</div>
				</div>
			) : entry}
			{fractionProblem ? <div className='element-field__error' role='alert'>{fractionProblem}</div> : null}
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//
//   BOOLEAN (YES / NO)
//
//////////////////////////////////////////////////

/**
 * A yes/no element: an antd Switch with the state it is currently IN spelled
 * out beside it, under the standard field label.
 *
 * Not a ToggleRow (the platform's treatment for settings and options): this
 * is a data field of an item, and it sits in the same left-aligned
 * label-over-control run as every other element on the form. The word beside
 * the switch is what makes it readable — a lone switch says only "on", never
 * "Paid", and the card author may have named the states anything.
 *
 * Emits a real boolean, always: there is no third state to enter. An item
 * saved before the element existed reads as absent ("not set") and the
 * switch shows the false label until it is touched.
 */
export function InputBoolean ( { elementName, elementLabel, help = '', value, onChange,
	disabled = false, labelBadge = null, element = null } ) {

	const { trueLabel, falseLabel } = booleanLabels( element );
	const checked = value === true;

	const handleChange = next => onChange( elementName, next );

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			help={help}>
			<div className='input-boolean'>
				<Switch
					id={elementName}
					checked={checked}
					onChange={handleChange}
					disabled={disabled}
					aria-label={typeof elementLabel === 'string' ? elementLabel : undefined}
				/>
				<span className='input-boolean__state'>{checked ? trueLabel : falseLabel}</span>
			</div>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//
//   PHONE
//
//////////////////////////////////////////////////

const toText = v => ( v === null || v === undefined ? '' : String( v ) );

/**
 * A phone element: one input that a person types a number into however they
 * normally write it, and that STORES the one spelling the platform reads back
 * (E.164, '+12025550123').
 *
 * The bargain is made on BLUR, which is the moment the number is finished:
 * a number that parses is rewritten in the field as the reader would dial it
 * — national at home, international abroad — and the E.164 goes to the form.
 * A number that does not parse is left exactly as typed, and the field's
 * validator (utils/elementValidation) says so under it; nothing is silently
 * discarded or half-corrected while someone is still typing.
 *
 * The element's region rides INSIDE the input as a passive suffix, the app's
 * affix convention (docs/platform-standards.md §7): "202 555 0123" is a
 * different number in two countries, and the field should say which one it is
 * reading before the message has to.
 */
export function InputPhone ( { elementName, elementLabel, help = '', value, onChange,
	disabled = false, labelBadge = null, required = false, element = null,
	status = undefined, onBlur = undefined, allowAutocomplete = true } ) {

	const country = normalizeCountry( element?.options?.country ) || DEFAULT_COUNTRY;

	// What the FIELD shows for a stored value: the dialled spelling when the
	// number is real, the stored characters when it is not (a legacy value
	// from before the type existed reads as it was entered, never rewritten).
	const displayOf = useCallback( raw => {
		const text = toText( raw );
		if ( ! text.trim() ) return '';
		const parsed = parsePhone( text, country );
		return parsed.valid ? formatPhone( parsed.e164, country ) : text;
	}, [ country ] );

	const [ text, setText ] = useState( () => displayOf( value ) );
	// The last value THIS input emitted. A prop that matches it is our own
	// keystroke coming back, and re-deriving the text from it would fight the
	// typist (a half-typed number that happens to parse would reformat under
	// the cursor); a prop that does not is the form loading, resetting or
	// being driven from elsewhere, and the field follows it.
	const emittedRef = useRef( value );

	useEffect( () => {
		if ( value === emittedRef.current ) return;
		emittedRef.current = value;
		setText( displayOf( value ) );
	}, [ value, displayOf ] );

	const emit = next => {
		emittedRef.current = next;
		onChange( elementName, next );
	};

	const handleChange = event => {
		setText( event.target.value );
		emit( event.target.value );
	};

	const handleBlur = event => {
		const parsed = parsePhone( text, country );
		if ( parsed.valid ) {
			setText( formatPhone( parsed.e164, country ) );
			emit( parsed.e164 );
		} else {
			const trimmed = text.trim();
			if ( trimmed !== text ) {
				setText( trimmed );
				emit( trimmed );
			}
		}
		if ( onBlur ) onBlur( event );
	};

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			<Input
				type='tel'
				name={elementName}
				id={elementName}
				size='large'
				className='w-100'
				disabled={disabled}
				required={required}
				status={status}
				value={text}
				onChange={handleChange}
				onBlur={handleBlur}
				inputMode='tel'
				autoCapitalize='none'
				suffix={<span className='input-passive-suffix'>{country}</span>}
				{...getAutofillProps( 'tel', allowAutocomplete )}
			/>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//
//   EMAIL
//
//////////////////////////////////////////////////

/**
 * An email element. There is nothing to reformat — an address reads as it is
 * stored — so the only thing blur does is trim the stray space a paste brings
 * along, which is the difference between "this is not an address" and saving.
 *
 * The DOMAIN is lower-cased by the API on write, not here: folding while
 * someone types would move the cursor and fight a capital they are about to
 * delete anyway, and the shared module (elementFormat/email) is what both
 * sides agree with.
 */
export function InputEmail ( { elementName, elementLabel, help = '', value, onChange,
	disabled = false, labelBadge = null, required = false, placeholder = null,
	status = undefined, onBlur = undefined, allowAutocomplete = true } ) {

	const handleChange = event => onChange( elementName, event.target.value );

	const handleBlur = event => {
		const text = toText( value );
		const trimmed = text.trim();
		if ( trimmed !== text ) onChange( elementName, trimmed );
		if ( onBlur ) onBlur( event );
	};

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			<Input
				type='email'
				name={elementName}
				id={elementName}
				size='large'
				className='w-100'
				placeholder={placeholder}
				disabled={disabled}
				required={required}
				status={status}
				value={toText( value )}
				onChange={handleChange}
				onBlur={handleBlur}
				inputMode='email'
				autoCapitalize='none'
				autoCorrect='off'
				{...getAutofillProps( 'email', allowAutocomplete )}
			/>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//
//   LOCATION
//
//////////////////////////////////////////////////

const LOCATION_PRECISION = 6;

// A coordinate the browser handed us, at the precision the platform stores:
// six places is ~10cm, past anything a phone's GPS actually knows.
const roundCoord = n => ( Number.isFinite( n ) ? Number( n.toFixed( LOCATION_PRECISION ) ) : null );

const finiteOrNullCoord = v => ( typeof v === 'number' && Number.isFinite( v ) ? v : null );

/**
 * A location element: an address, a pair of coordinates, or both — one field
 * (docs/plans/data-types.md decision 5).
 *
 * THE LAYOUT, and why. The address is the row: it is what a person writes
 * down and what they recognise later, so it gets the full width with **Use my
 * location** as the rightmost member of the compact group (the affix
 * convention — actions rightmost). Coordinates are the exception, not the
 * rule, so they wait behind a **Coordinates** toggle row directly beneath,
 * already open when the value has some. On a phone that is one input, one
 * icon button, and one 14px switch row; nothing squeezes and nothing wraps
 * mid-run. An element that asks for `accept: 'coordinates'` skips the address
 * entirely and shows the two numbers outright — hiding the only thing it
 * accepts behind a toggle would be a puzzle, not a form.
 *
 * Pasting "37.7749, -122.4194" into the ADDRESS box files it as coordinates,
 * which is the case decision 5 exists for: the shared parser decides, on
 * blur, so it happens once the paste is finished rather than mid-keystroke.
 *
 * Emits the contract's shape — `{ address?, lat?, lng? }` with only the keys
 * that are set — or '' when there is nothing. HALF a coordinate is emitted as
 * it stands, so the validator can say "both or neither" instead of the field
 * quietly dropping what someone typed.
 */
export function InputLocation ( { elementName, elementLabel, help = '', value, onChange,
	disabled = false, labelBadge = null, required = false, element = null,
	status = undefined, onBlur = undefined, allowAutocomplete = true } ) {

	const accept = [ 'both', 'address', 'coordinates' ].includes( element?.options?.accept )
		? element.options.accept
		: 'both';
	const showsAddress = accept !== 'coordinates';
	const showsCoords = accept !== 'address';

	const readValue = useCallback( raw => {
		if ( typeof raw === 'string' ) {
			// A bare string is the legacy (and empty) spelling: an address.
			return { address: raw, lat: null, lng: null };
		}
		if ( ! raw || typeof raw !== 'object' ) return { address: '', lat: null, lng: null };
		return {
			address: typeof raw.address === 'string' ? raw.address : '',
			lat: finiteOrNullCoord( raw.lat ),
			lng: finiteOrNullCoord( raw.lng ),
		};
	}, [] );

	const [ state, setState ] = useState( () => readValue( value ) );
	const [ coordsOpen, setCoordsOpen ] = useState( () => {
		const read = readValue( value );
		return read.lat !== null || read.lng !== null;
	} );
	const emittedRef = useRef( value );

	useEffect( () => {
		if ( value === emittedRef.current ) return;
		emittedRef.current = value;
		const read = readValue( value );
		setState( read );
		if ( read.lat !== null || read.lng !== null ) setCoordsOpen( true );
	}, [ value, readValue ] );

	// The stored shape, built from what is actually filled in.
	const compose = next => {
		const address = ( next.address || '' ).trim();
		const out = {};
		if ( address ) out.address = address;
		if ( next.lat !== null ) out.lat = next.lat;
		if ( next.lng !== null ) out.lng = next.lng;
		return Object.keys( out ).length ? out : '';
	};

	const commit = next => {
		setState( next );
		const composed = compose( next );
		emittedRef.current = composed;
		onChange( elementName, composed );
	};

	const handleAddressChange = event => commit( { ...state, address: event.target.value } );

	const handleAddressBlur = event => {
		// "37.7749, -122.4194" pasted into the address box IS a pair of
		// coordinates; anything else stays the address it looks like. An
		// element that takes only an ADDRESS is the exception: it has nowhere
		// to put a point, so reading one out of the box would blank the field
		// and store a value the element itself refuses.
		const parsed = showsCoords ? parseLocation( ( state.address || '' ).trim() ) : { valid: false };
		if ( parsed.valid && parsed.value && parsed.value.lat !== undefined && ! parsed.value.address ) {
			setCoordsOpen( true );
			commit( { address: '', lat: parsed.value.lat, lng: parsed.value.lng } );
		} else {
			const trimmed = ( state.address || '' ).trim();
			if ( trimmed !== state.address ) commit( { ...state, address: trimmed } );
		}
		if ( onBlur ) onBlur( event );
	};

	const handleCoord = key => next => commit( { ...state, [ key ]: finiteOrNullCoord( next ) } );

	const geolocationAvailable = geolocation.available();
	const [ locating, setLocating ] = useState( false );

	const handleUseMyLocation = () => {
		if ( ! geolocationAvailable || locating ) return;
		setLocating( true );
		geolocation.current( { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 } ).then(
			position => {
				setLocating( false );
				const lat = roundCoord( position?.coords?.latitude );
				const lng = roundCoord( position?.coords?.longitude );
				if ( lat === null || lng === null ) {
					message.error( 'Your browser did not return a usable position.' );
					return;
				}
				setCoordsOpen( true );
				// The address is kept: "home" plus the point it is at is a
				// better record than either alone, and an element that only
				// takes coordinates has no address to keep.
				commit( { address: showsAddress ? state.address : '', lat, lng } );
			},
			() => {
				setLocating( false );
				message.error( 'Location is unavailable. Check the site\'s location permission, or type the coordinates.' );
			},
		);
	};

	const label = typeof elementLabel === 'string' ? elementLabel : ( element?.label?.singular || elementName );

	// The button's whole job is to set a POINT, so an element that takes only
	// an address has no use for it — and the browser permission prompt it
	// raises should never appear on a field that could not store the answer.
	const locateButton = geolocationAvailable && showsCoords ? (
		<Tooltip title='Use my location'>
			<Button
				size='large'
				icon={<GEO_PIN />}
				disabled={disabled}
				loading={locating}
				onClick={handleUseMyLocation}
				aria-label={`Use my location for ${label}`}
			/>
		</Tooltip>
	) : null;

	const coordInputs = (
		<div className='input-location__coords'>
			<InputNumber
				id={`${elementName}-lat`}
				size='large'
				className='input-location__coord'
				disabled={disabled}
				value={state.lat}
				onChange={handleCoord( 'lat' )}
				onBlur={onBlur}
				min={-LAT_LIMIT}
				max={LAT_LIMIT}
				precision={LOCATION_PRECISION}
				controls={false}
				inputMode='decimal'
				placeholder='Latitude'
				aria-label={`${label} latitude`}
				{...getAutofillProps()}
			/>
			<InputNumber
				id={`${elementName}-lng`}
				size='large'
				className='input-location__coord'
				disabled={disabled}
				value={state.lng}
				onChange={handleCoord( 'lng' )}
				onBlur={onBlur}
				min={-LNG_LIMIT}
				max={LNG_LIMIT}
				precision={LOCATION_PRECISION}
				controls={false}
				inputMode='decimal'
				placeholder='Longitude'
				aria-label={`${label} longitude`}
				{...getAutofillProps()}
			/>
			{showsAddress ? null : locateButton}
		</div>
	);

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			<div className='input-location'>
				{showsAddress ? (
					<Space.Compact className='input-affix-group' block>
						<Input
							type='text'
							name={elementName}
							id={elementName}
							size='large'
							className='input-affix-group__input'
							disabled={disabled}
							required={required}
							status={status}
							value={state.address}
							onChange={handleAddressChange}
							onBlur={handleAddressBlur}
							placeholder={showsCoords ? 'Address, or "latitude, longitude"' : 'Address'}
							{...getAutofillProps( 'street-address', allowAutocomplete )}
						/>
						{locateButton}
					</Space.Compact>
				) : null}
				{showsCoords && showsAddress ? (
					<ToggleRow
						label='Coordinates'
						description='A point on the map, when the address is not enough.'
						checked={coordsOpen}
						disabled={disabled}
						onChange={next => {
							setCoordsOpen( next );
							// Closing the row clears the point: a coordinate
							// nobody can see is a coordinate nobody agreed to
							// save.
							if ( ! next && ( state.lat !== null || state.lng !== null ) ) {
								commit( { ...state, lat: null, lng: null } );
							}
						}}
					/>
				) : null}
				{showsCoords && ( coordsOpen || ! showsAddress ) ? coordInputs : null}
			</div>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//
//   MEASUREMENT
//
//////////////////////////////////////////////////

// The magnitude an InputNumber reports, or null for blank/NaN.
const finiteOrNull = v => ( typeof v === 'number' && Number.isFinite( v ) ? v : null );

/**
 * The bare measurement control: a number (two for a composite unit such as
 * lb-oz) with the unit symbol INSIDE the input as a passive suffix, and the
 * unit picker as the rightmost member of the Space.Compact group — the app's
 * affix convention (docs/platform-standards.md).
 *
 * Emits `{ value, unit }` (a composite's value is major + minor/minorPerMajor,
 * the shared module's storage shape) or null when the magnitude is blank; the
 * API computes `base` on save. Deliberately NO conversion on a unit switch:
 * the typed number stays as typed, because "150" then picking kg means the
 * person meant 150 kg, not 68 kg — the stored value is what they entered.
 *
 * Opening: a stored object opens in its stored unit (so 8 lb 7 oz re-edits as
 * 8 and 7, never 8.4375), a legacy bare number in the element's default unit,
 * a string through the shared parser. An EMPTY value opens in the unit last
 * used for this element in this browser, else the default.
 *
 * `onChange(next)` takes the value only; InputMeasurement below wraps this in
 * the form's (name, value) convention with a label.
 */
export function MeasurementControl ( { value, onChange, element, disabled = false, size = 'large', id, 'aria-label': ariaLabel, cardId } ) {

	const rawOptions = element?.options;
	const options = useMemo( () => rawOptions || {}, [ rawOptions ] );
	const elementName = element?.name;
	const measure = getMeasure( options.measure );
	const allowed = useMemo( () => allowedUnitsFor( options ), [ options ] );
	const fallbackUnit = defaultUnitFor( options );

	const openingUnit = () => readLastUsedUnit( cardId, elementName, options ) || fallbackUnit;

	// KPR-1025: each part reads a typed fraction ("1 1/2", "¾"), and "Show as
	// a fraction" shows the number as one while it is not being typed in
	// (src/utils/numberEntry.js). A malformed fraction's sentence shows once
	// the part is left, until the next edit.
	const fractions = options.fractions === true;
	const typedRef = useRef( { major: '', minor: '' } );
	const [ fractionProblem, setFractionProblem ] = useState( null );
	const blurPart = part => () => setFractionProblem( fractionEntryProblem( typedRef.current[ part ] ) );

	// Local state is what the person is typing; the value prop is what the
	// form holds. They agree except mid-edit (a blank minor part, a unit with
	// nothing typed yet), so the prop only resets the state when it changes
	// to something this control did not itself emit.
	const [ state, setState ] = useState( () => fromValue( value, options, openingUnit ) );
	const emittedRef = useRef( value );

	useEffect( () => {
		// Same value by CONTENT, not identity: a parent that rebuilds the
		// object from what this control emitted (a filter row re-parsing its
		// own literal) must not reset a half-typed composite to "8 lb 0 oz".
		if ( sameMeasurementValue( value, emittedRef.current ) ) return;
		emittedRef.current = value;
		setState( prev => fromValue( value, options, () => prev.unit || openingUnit() ) );
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ value ] );

	// An EMPTY control follows the element definition: when the measure or
	// the allowlist changes under it (the card editor's default-value control
	// as the author picks a measure; a card definition reloading), a unit the
	// element no longer ACCEPTS is replaced, so what gets typed next is never
	// "5 lb" for a length. Accepts, not offers: a rate's picker shows one time,
	// and clearing a stored 2 L/min on an mL/h element to type 3 must mean
	// 3 L/min, never 3 mL/h (utils/measurements acceptsUnit). A stored value
	// keeps its unit (see unitOptions).
	const empty = state.major === null && state.minor === null;
	useEffect( () => {
		if ( ! empty || ! state.unit || acceptsUnit( options, state.unit ) ) return;
		setState( prev => ( { ...prev, unit: openingUnit() } ) );
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ options, empty, state.unit ] );

	const emit = next => {
		const composite = compositeParts( next.unit );
		let out = null;
		if ( composite ) {
			if ( next.major !== null || next.minor !== null ) {
				out = { value: joinComposite( next.major || 0, next.minor || 0, next.unit ), unit: next.unit };
			}
		} else if ( next.major !== null ) {
			out = { value: next.major, unit: next.unit };
		}
		// Picking a unit with nothing typed changes nothing the form holds:
		// reporting null over an already-empty value ('' from the API, null
		// from a cleared field) would only mark the form dirty for no edit.
		if ( out === null && ( value === null || value === undefined || value === '' ) ) return;
		emittedRef.current = out;
		if ( out ) writeLastUsedUnit( cardId, elementName, next.unit );
		onChange( out );
	};

	const update = patch => {
		if ( fractionProblem ) setFractionProblem( null );
		setState( prev => {
			const next = { ...prev, ...patch };
			emit( next );
			return next;
		} );
	};

	const handleMajor = v => update( { major: finiteOrNull( v ) } );

	// 7 lb 16 oz is 8 lb 0 oz: the minor part carries into the major part
	// rather than being clamped, so typing "16" lands where arithmetic says.
	const handleMinor = v => {
		const minor = finiteOrNull( v );
		const composite = compositeParts( state.unit );
		if ( minor === null || ! composite || minor < composite.minorPerMajor ) {
			update( { minor } );
			return;
		}
		const carry = Math.floor( minor / composite.minorPerMajor );
		update( { major: ( state.major || 0 ) + carry, minor: minor - carry * composite.minorPerMajor } );
	};

	// The magnitude stays as typed; only the minor part is dropped or blanked
	// when the unit changes shape (a single unit has none).
	const handleUnit = unitId => {
		writeLastUsedUnit( cardId, elementName, unitId );
		update( { unit: unitId, minor: compositeParts( unitId ) ? state.minor : null } );
	};

	if ( ! measure || ! state.unit ) {
		// A measurement element without a valid measure cannot be entered;
		// the card definition is where that is fixed.
		return <div className='element-field__driven-value'>Measurement settings are incomplete for this element.</div>;
	}

	// The stored unit is offered even when the allowlist no longer holds it,
	// so an old value reads and re-saves in the unit it was entered in.
	// Grouped by family for a measure that splits (volume offers 31 units,
	// including two different gallons), flat for every other measure.
	// The picked unit's own measure decides whether a minus sign is taken: on an
	// element that allows several measures (KPR-1085) a temperature may go
	// below zero where a mass may not.
	const allowNegative = !! ( getMeasure( getUnit( state.unit )?.measure ) || measure )?.allowNegative;
	const storedUnit = allowed.some( u => u.id === state.unit ) ? null : getUnit( state.unit );
	const unitOptions = groupUnitOptions( storedUnit ? [ ...allowed, storedUnit ] : allowed );
	const composite = compositeParts( state.unit );
	const inputId = id || elementName;
	const label = ariaLabel || element?.label?.singular || elementName;

	const partInputMode = fractions ? 'text' : getNumericInputMode( 'measurement' );
	// Each part holds its own number, so each re-reads its own shown text.
	const majorEntryProps = numberEntryProps( { fractions, value: state.major } );
	const minorEntryProps = numberEntryProps( { fractions, value: state.minor } );

	return ( <>
		<Space.Compact className='input-affix-group input-measurement' block>
			<InputNumber
				id={inputId}
				name={inputId}
				size={size}
				className='input-affix-group__input input-measurement__part'
				disabled={disabled}
				value={state.major}
				onChange={handleMajor}
				onInput={text => { typedRef.current.major = text; }}
				onBlur={blurPart( 'major' )}
				status={fractionProblem ? 'error' : undefined}
				{...majorEntryProps}
				min={allowNegative ? undefined : 0}
				controls={false}
				inputMode={partInputMode}
				suffix={<span className='input-measurement__symbol'>{composite ? composite.major.symbol : unitSymbol( state.unit, options )}</span>}
				aria-label={composite ? `${label} (${composite.major.label.toLowerCase()})` : label}
				{...getAutofillProps()}
			/>
			{composite ? (
				<InputNumber
					id={`${inputId}-minor`}
					name={`${inputId}-minor`}
					size={size}
					className='input-affix-group__input input-measurement__part'
					disabled={disabled}
					value={state.minor}
					onChange={handleMinor}
					onInput={text => { typedRef.current.minor = text; }}
					onBlur={blurPart( 'minor' )}
					status={fractionProblem ? 'error' : undefined}
					{...minorEntryProps}
					min={0}
					step={1}
					controls={false}
					inputMode={partInputMode}
					suffix={<span className='input-measurement__symbol'>{composite.minor.symbol}</span>}
					aria-label={`${label} (${composite.minor.label.toLowerCase()})`}
					{...getAutofillProps()}
				/>
			) : null}
			<Select
				id={`${inputId}-unit`}
				size={size}
				className='input-measurement__unit'
				disabled={disabled}
				value={state.unit}
				onChange={handleUnit}
				options={unitOptions}
				// Typeahead over every spelling the registry knows for a unit
				// (label, symbol, aliases — utils/measurements unitOption):
				// "lb" or "pound" finds pounds in a 31-unit volume list too.
				showSearch
				filterOption={optionSearchFilter}
				// Closed control: the abbreviation only, so the width follows the
				// text; the open list keeps 'Name (abbr)'. Not `optionLabelProp`,
				// which would need a second label field on every option.
				labelRender={( { value: unitId } ) => unitSymbol( unitId, options )}
				popupMatchSelectWidth={false}
				aria-label={`${label} unit`}
			/>
		</Space.Compact>
		{fractionProblem ? <div className='element-field__error' role='alert'>{fractionProblem}</div> : null}
	</> );

}

// Two value props that mean the same entry (identical, or both `{ value, unit }` with equal parts).
function sameMeasurementValue ( a, b ) {
	if ( a === b ) return true;
	if ( ! isMeasurementValue( a ) || ! isMeasurementValue( b ) ) return false;
	return a.value === b.value && a.unit === b.unit;
}

// Value prop -> control state. `pickUnit` supplies the unit for an empty value.
function fromValue ( raw, options, pickUnit ) {
	const entry = readEntryValue( raw, options );
	if ( ! entry ) return { unit: pickUnit(), major: null, minor: null };
	const composite = compositeParts( entry.unit );
	if ( composite ) {
		const { major, minor } = splitComposite( entry.value, entry.unit );
		return { unit: entry.unit, major, minor };
	}
	return { unit: entry.unit, major: entry.value, minor: null };
}

/**
 * The form-facing measurement input: the same label wrapper and
 * (name, value) onChange every other element input uses, around
 * MeasurementControl. `element` is the element definition (its options carry
 * the measure and units); `cardId` scopes the remembered unit.
 */
export function InputMeasurement ( { elementName, elementLabel, help = '', value, onChange, element,
	disabled = false, labelBadge = null, required = false, cardId } ) {

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			<MeasurementControl
				id={elementName}
				value={value}
				onChange={next => onChange( elementName, next )}
				element={element}
				disabled={disabled}
				cardId={cardId}
			/>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//
//   CURRENCY
//
//////////////////////////////////////////////////

/**
 * The bare currency control (docs/plans/currency.md): an amount, and the
 * currency it is in.
 *
 *  - ONE allowed currency: the amount input alone, with the ISO code as a
 *    passive suffix INSIDE it — it is a label, not a control.
 *  - SEVERAL: the currency Select is the rightmost member of the
 *    Space.Compact group, the app's affix convention for a control
 *    (docs/platform-standards.md); it searches once there are more than
 *    five. Options read "USD — US dollar"; the closed picker shows the code.
 *
 * The amount is an antd InputNumber in stringMode at the currency's own
 * decimals (USD 2, JPY 0, BHD 3), grouped en-US when not being typed. A
 * decimal past the currency's precision never enters the value — it is cut,
 * not rounded, so 19.999 cannot quietly become 20.00. Emits `{ amount,
 * currency }` with `amount` in MINOR units computed on the decimal TEXT
 * (19.99 -> 1999 exactly; never a float multiplied by 100), or null when
 * the amount is blank. An amount too large to store is emitted as its text
 * with the code ("…  USD"), which the field's validator refuses in the API's
 * words rather than this control guessing.
 *
 * A stored value opens in ITS currency even when the element no longer
 * allows it (that code is added to the picker), so re-editing never
 * silently re-denominates it. Switching currency keeps the typed number,
 * re-rounded (half away from zero) to the new currency's decimals.
 *
 * `onChange(next)` takes the value only; InputCurrency below wraps this in
 * the form's (name, value) convention with a label.
 */
export function CurrencyControl ( { value, onChange, element, disabled = false, size = 'large', id, 'aria-label': ariaLabel, status = undefined, onBlur = undefined } ) {

	const rawOptions = element?.options;
	const options = useMemo( () => rawOptions || {}, [ rawOptions ] );
	const elementName = element?.name;
	const fallback = defaultCurrencyFor( options );

	const fromValue = ( raw, pickCode ) => {
		const read = readEntryCurrency( raw, options );
		if ( read ) return { currency: read.currency, text: amountTextOf( read ) || null };
		return { currency: pickCode(), text: null };
	};

	const [ state, setState ] = useState( () => fromValue( value, () => fallback ) );
	const emittedRef = useRef( value );

	useEffect( () => {
		if ( sameCurrencyValue( value, emittedRef.current ) ) return;
		emittedRef.current = value;
		setState( prev => fromValue( value, () => prev.currency || fallback ) );
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ value ] );

	// An EMPTY control follows the element definition (the card editor
	// changing the list under a default-value control): a code the element
	// no longer offers is replaced. A stored value keeps its currency.
	const allowed = useMemo( () => entryCurrencies( options ), [ options ] );
	useEffect( () => {
		if ( state.text !== null || ! fallback || allowed.includes( state.currency ) ) return;
		setState( prev => ( { ...prev, currency: fallback } ) );
	}, [ allowed, fallback, state.text, state.currency ] );

	const emit = next => {
		let out = null;
		if ( next.text !== null && next.currency ) {
			const entry = entryFromText( next.text, next.currency );
			if ( entry && entry.error ) out = `${entry.text} ${next.currency}`;
			else out = entry;
		}
		if ( out === null && ( value === null || value === undefined || value === '' ) ) return;
		emittedRef.current = out;
		onChange( out );
	};

	const update = patch => {
		setState( prev => {
			const next = { ...prev, ...patch };
			emit( next );
			return next;
		} );
	};

	const exponent = exponentOf( state.currency ) ?? 2;

	// Stable identities: rc-input-number re-renders its text whenever the
	// formatter changes, which on a fresh closure per render would re-pad
	// "12." to "12.00" under the cursor. Grouped when read, raw while typed.
	// (A formatter replaces antd's own precision padding, so it pads: "12.5"
	// reads "12.50" in USD.)
	const currencyCode = state.currency;
	const amountFormatter = useCallback(
		( v, info ) => ( info?.userTyping ? info.input : groupAmountText( paddedAmountText( v, currencyCode ) ) ),
		[ currencyCode ]
	);
	const amountParser = useCallback(
		v => ( v ? clampDecimals( String( v ).replace( /,/g, '' ), exponent ) : '' ),
		[ exponent ]
	);

	const handleAmount = v => {
		const text = v === null || v === undefined || v === '' ? null : clampDecimals( String( v ), exponent );
		update( { text } );
	};

	// The typed number survives a currency switch, re-rounded to the new
	// currency's decimals — the same rounding the display applies.
	const handleCurrency = code => {
		let text = state.text;
		if ( text !== null ) {
			const minor = toMinor( text, code, 'round' );
			text = minor === null ? text : majorText( minor, code );
		}
		update( { currency: code, text } );
	};

	if ( ! state.currency ) {
		// A currency element with no currencies cannot be entered; the card
		// definition is where that is fixed.
		return <div className='element-field__driven-value'>Currency settings are incomplete for this element.</div>;
	}

	const codes = entryCurrencies( options, state.currency );
	const multiple = codes.length > 1;
	const inputId = id || elementName;
	const label = ariaLabel || element?.label?.singular || elementName;
	const nonNegative = options.nonNegative === true;

	const amountInput = (
		<InputNumber
			id={inputId}
			name={inputId}
			size={size}
			className={multiple ? 'input-affix-group__input input-currency__amount' : 'w-100 input-currency__amount'}
			disabled={disabled}
			value={state.text}
			onChange={handleAmount}
			onBlur={onBlur}
			status={status}
			stringMode
			precision={exponent}
			min={nonNegative ? 0 : undefined}
			controls={false}
			inputMode={exponent ? 'decimal' : 'numeric'}
			formatter={amountFormatter}
			parser={amountParser}
			suffix={multiple ? undefined : <span className='input-passive-suffix input-currency__code'>{state.currency}</span>}
			aria-label={label}
			{...getAutofillProps()}
		/>
	);

	if ( ! multiple ) return <div className='input-currency'>{amountInput}</div>;

	return (
		<Space.Compact className='input-affix-group input-currency' block>
			{amountInput}
			<Select
				id={`${inputId}-currency`}
				size={size}
				className='input-currency__code-select'
				disabled={disabled}
				value={state.currency}
				onChange={handleCurrency}
				options={codes.map( currencyOption ).filter( Boolean )}
				showSearch={codes.length > CURRENCY_SEARCH_THRESHOLD}
				filterOption={currencySearchFilter}
				labelRender={( { value: code } ) => code}
				popupMatchSelectWidth={false}
				aria-label={`${label} currency`}
			/>
		</Space.Compact>
	);

}

// An amount's text at its currency's decimals ("12.5" -> "12.50" for USD); '' when blank.
function paddedAmountText ( v, code ) {
	if ( v === undefined || v === null || v === '' ) return '';
	const minor = toMinor( String( v ), code, 'round' );
	return minor === null ? String( v ) : majorText( minor, code );
}

// Two value props that mean the same entry (identical, or both `{ amount, currency }` with equal parts).
function sameCurrencyValue ( a, b ) {
	if ( a === b ) return true;
	if ( ! isCurrencyValue( a ) || ! isCurrencyValue( b ) ) return false;
	return a.amount === b.amount && a.currency === b.currency;
}

/**
 * The form-facing currency input: the same label wrapper and (name, value)
 * onChange every other element input uses, around CurrencyControl.
 */
export function InputCurrency ( { elementName, elementLabel, help = '', value, onChange, element,
	disabled = false, labelBadge = null, required = false, status = undefined, onBlur = undefined } ) {

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			<CurrencyControl
				id={elementName}
				value={value}
				onChange={next => onChange( elementName, next )}
				element={element}
				disabled={disabled}
				status={status}
				onBlur={onBlur}
			/>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//   
//   SWITCH
//   
//////////////////////////////////////////////////

export function InputSwitch ( { elementName, elementLabel, value, onChange,
	disabled = false, labelBadge = null, description = null, first = false, help = '' } ) {

	const handleChange = checked => onChange( elementName, checked );

	// Every boolean is a toggle ROW: label + one-line description left, the
	// switch right (ToggleRow). A run of InputSwitches inside one panel reads
	// as a divided list of settings.
	const label = (
		<FieldHelp text={help} label={elementLabel}>
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
				{labelBadge}
				<span>{elementLabel}</span>
			</span>
		</FieldHelp>
	);

	return (
		<ToggleRow
			id={elementName}
			name={elementName}
			label={label}
			description={description}
			checked={!!value}
			onChange={handleChange}
			disabled={disabled}
			first={first}
			aria-label={typeof elementLabel === 'string' ? elementLabel : undefined}
		/>
	);

}



//////////////////////////////////////////////////
//   
//   SELECT
//   
//////////////////////////////////////////////////

// The platform's option matcher for searchable Selects: utils/optionSearchFilter.js.
export { foldSearchText };
export { optionSearchFilter };

/**
 * Choice fields with 2–5 options render as a segmented control instead of
 * a dropdown (platform form spec) — every option visible, one tap to pick.
 * An optional field gets a leading — segment so it can be cleared.
 */
export function InputSegmented ( props ) {
	const { elementName, value, onChange,
		disabled = false,
		required = false,
		options = [],
	} = props;

	const EMPTY = '__none__';

	const segmentedOptions = useMemo( () => {
		const mapped = options.map( choice => {
			// A library icon, drawn on demand (KPR-1187); nothing for a name the library does not have.
			const icon = isLibraryIconName( choice?.icon ) ? <LibraryIcon name={ choice.icon } aria-hidden='true' /> : null;
			return {
				value: choice.value,
				label: (
					<span className='input-segmented__option'>
						{choice?.color ? (
							<span className='input-segmented__dot' style={{ backgroundColor: choice.color }} />
						) : null}
						{icon}
						<span>{choice.label ?? choice.value}</span>
					</span>
				),
			};
		} );
		if ( ! required ) {
			mapped.unshift( { value: EMPTY, label: '—' } );
		}
		return mapped;
	}, [ options, required ] );

	// The — segment says "nothing", and the platform spells nothing as ''.
	// `undefined` is not a value: JSON.stringify DROPS the key, so a cleared
	// field never reaches the API at all and the write has to guess what
	// silence meant. Clearing must be something the wire can carry.
	const handleChange = next => onChange( elementName, next === EMPTY ? '' : next );

	return (
		<Wrapper {...props}>
			<div id={elementName}>
				<Segmented
					className='input-segmented'
					options={segmentedOptions}
					value={value === undefined || value === null || value === '' ? ( required ? undefined : EMPTY ) : value}
					onChange={handleChange}
					disabled={disabled}
				/>
			</div>
		</Wrapper>
	);
}

export function InputSelect ( props ) {
	const { elementName, elementLabel, value, onChange,
		disabled = false,
		required = false,
		placeholder = null,
		options = [],
		showSearch = true,
		allowClear = true,
		labelInline = false,
		selectIcons = false,
		labelBadge = null,
		// Opt out with `false`, or hand in a bespoke matcher; otherwise every
		// InputSelect gets the shared searchText-aware filter.
		filterOption = optionSearchFilter,
		// antd's own validation border ('warning' | 'error'); the filter
		// builder flags a stored value that is not one of the choices.
		status,
	} = props;

	// What the ✕ reports. '' for every ordinary field — see below. A form
	// whose clear is a DESTRUCTIVE act rather than an edit passes
	// `clearValue={undefined}` on purpose, so the key keeps falling out of the
	// body until that form has a confirmation to put in front of it; the one
	// caller doing that today is the card editor's parent-card select, where
	// detaching a parent drops the inherited elements (and, on the next item
	// save, their stored values) from the whole subtree.
	//
	// Read off `props` rather than destructured with a default, because a
	// default would turn the explicit `undefined` that opts out back into the
	// '' it is opting out of.
	const clearValue = Object.prototype.hasOwnProperty.call( props, 'clearValue' ) ? props.clearValue : '';

	// antd's clear button reports `undefined`, which JSON.stringify drops from
	// the body — the same silent hole InputSegmented had. '' is the spelling
	// every other field uses for nothing, and the one the wire can carry; the
	// icon-tile branch below deselects with `null`, which survives JSON and
	// which the API reads as empty too.
	const handleChange = value => onChange( elementName, value === undefined ? clearValue : value );



	const MAX_SELECT_ICONS = 6;

	const showSelectIcons = !! selectIcons && options.length <= MAX_SELECT_ICONS;

	const displayType = showSelectIcons ? 'selectIcons' : 'select';



	// Select Icons
	const DEFAULT_BACKGROUND_COLOR = 'var(--chip-bg)';
	const DEFAULT_BORDER_COLOR = 'var(--border-control)';
	const DEFAULT_BACKGROUND_COLOR_SELECTED = 'var(--chip-bg-active)';
	const DEFAULT_BORDER_COLOR_SELECTED = 'var(--ink)';
	


	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelInline={labelInline}
			labelBadge={labelBadge}
			required={required}
			help={props.help}>

			{displayType === 'select' ? <Select
				name={elementName}
				id={elementName}
				// id={'input-' + elementName}
				size='large'
				className={'w-100 ' + ( labelInline ? 'my-auto' : '' )}
				style={{minWidth: '180px'}}
				placeholder={placeholder}
				disabled={disabled}
				required={required}
				value={value}
				onChange={handleChange}
				options={options}
				allowClear={allowClear}
				filterOption={filterOption}
				showSearch={showSearch}
				status={status}
			/> : null}

			{displayType === 'selectIcons' ? <>
			<div
				className='d-flex flex-wrap justify-content-start'
			>
				{options.map( choice => {
					// Choices from card definitions store the icon by export
					// name: a library icon, drawn on demand (KPR-1187).
					const libraryIcon = ! choice?.iconJSX && isLibraryIconName( choice?.icon );
					return <div className='d-inline' key={choice.value ?? choice.label}>
						<Button
							className='m-1'
							style={{
								backgroundColor: ( value === choice.value ? choice?.color || DEFAULT_BACKGROUND_COLOR_SELECTED : DEFAULT_BACKGROUND_COLOR ),
								border: '3px solid ' + ( choice?.color ? choice?.color : ( value === choice.value ? DEFAULT_BORDER_COLOR_SELECTED : DEFAULT_BORDER_COLOR ) ),
								height: '3em',
								minWidth: '3em',
								color: FONT_COLOR_DARK,
							}}
							onClick={() => choice.value === value ? handleChange( null ) : handleChange( choice.value )}
						>
							{choice?.iconJSX || ( libraryIcon ? <LibraryIcon name={ choice.icon } /> : null )}
							{choice.label}
						</Button>
					</div>;
				} )}
			</div>
			</> : null}

		</Wrapper>
	</>;

}



/**
 * A Choice that allows multiple values ("Allow multiple", docs/plans/data-
 * types-second-pass.md §3.5): antd's multiple Select over the choice chips —
 * each choice's colour and icon through the one renderer every choice control
 * uses (common/ChoiceOptions buildChoiceOptions), so a status known as a red
 * dot is a red dot here too. The picked choices sit in the field as chips that
 * wrap onto further lines (a phone reads every one).
 *
 * What it REPORTS is the stored shape: the picked values once each, in the
 * CHOICES' order (ruling D2) — the order the API canonicalises to, so the
 * form never shows one order and saves another — and '' when nothing is
 * picked, the platform's one spelling of nothing (an empty list would be
 * stored as '' anyway). A scalar it is handed (a value stored before the
 * switch) reads as one chip; an entry whose choice was since removed stays a
 * chip, shown as stored, and saves unchanged (the API's echo).
 */
export function InputChoiceMulti ( props ) {
	const { elementName, elementLabel, value, onChange,
		disabled = false,
		required = false,
		placeholder = 'Choose any',
		options = [],
		labelBadge = null,
	} = props;

	const selectOptions = useMemo( () => buildChoiceOptions( options ), [ options ] );
	const element = useMemo( () => ( { dataType: 'choice', options: { choices: options } } ), [ options ] );
	const current = choiceEntries( value );

	const handleChange = next => {
		const ordered = canonicalChoices( element, Array.isArray( next ) ? next : [] );
		onChange( elementName, ordered.length ? ordered : '' );
	};

	return (
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={props.help}>
			<Select
				mode='multiple'
				id={elementName}
				size='large'
				className='w-100 input-choice-multi'
				placeholder={placeholder}
				disabled={disabled}
				value={current}
				onChange={handleChange}
				options={selectOptions}
				allowClear
				showSearch
				filterOption={optionSearchFilter}
			/>
		</Wrapper>
	);
}



//////////////////////////////////////////////////
//   
//   CARD LOOKUP
//   
//////////////////////////////////////////////////

export function InputCardLookup ( {
	elementName,
	elementLabel,
	help = '',
	value,
	onChange,
	collectionId,
	elementOptions = {},
	disabled = false,
	required = false,
	placeholder = null,
	labelBadge = null,
	showMe = true,
} ) {

	useDateFormat(); // lookup previews format dates; re-render on preference change
	const navigate = useNavigate();
	const targetCardId = elementOptions?.lookupCardId;
	const allowMultiple = !!elementOptions?.allowMultiple;

	// THE PICKER FILTER (utils/lookupFilter.js; docs/plans/
	// data-types-second-pass.md §3.7, ruling D10). The options are fetched
	// with it ANDed into `?q=`. Not strict, "Show all" opens the whole card —
	// a person is never told an item does not exist because a filter hid it.
	// Strict, the API refuses a new value outside it, so nothing here offers
	// one: no Show all, and a quick create, a scan or "Me" that lands outside
	// it is said so rather than chosen.
	const lookupFilter = lookupFilterOf( elementOptions );
	const strictLookup = isStrictLookup( elementOptions );
	const [ showAll, setShowAll ] = useState( false );
	const narrowed = Boolean( lookupFilter ) && ( strictLookup || ! showAll );

	// WHERE THE RECORDS LIVE (docs/plans/personal-discover-global.md §4.5):
	// the target card's own collection when another collection OWNS it — a
	// published global collection — and this form's collection otherwise.
	// The card may not be in the cache yet (a global collection's cards
	// never come in the list); useEnsureCards fetches it.
	const cacheEarly = useOptionalCardDefinitions();
	const targetDef = targetCardId ? cacheEarly?.cardsById?.[ normalizeDbId( targetCardId ) ] : null;
	const targetOwner = targetDef?.scope === 'collection' ? normalizeDbId( targetDef.collection_id ) : '';
	const foreignTarget = Boolean( targetOwner ) && targetOwner !== normalizeDbId( collectionId );
	const sourceCollectionId = foreignTarget ? targetOwner : collectionId;
	// A SUB-COLLECTION's lookup into a card its parent owns (keepr-api §
	// Sub-collections): the records live in the parent, and are this family's
	// own — listed there (a reader of the parent sees them), never copied,
	// and opened where the viewer can open them.
	const contextCollection = useCollectionContext()?.collection || null;
	const familyContext = normalizeDbId( contextCollection?._id ) === normalizeDbId( collectionId ) ? contextCollection : null;
	// The quick-create posts into THIS collection, so its defaults read this
	// collection's zone (the owner's ruling B11).
	const quickCreateZone = collectionZone( familyContext );
	const familyTarget = foreignTarget && Boolean( familyContext ) && familyIdsOf( familyContext ).has( targetOwner );
	const ensureTargetIds = useMemo( () => ( targetCardId ? [ targetCardId ] : [] ), [ targetCardId ] );
	useEnsureCards( ensureTargetIds );
	const [ sourceMeta, setSourceMeta ] = useState( null );
	const [ copyOpen, setCopyOpen ] = useState( false );
	// A target another collection shares through a LINK (keepr-api § Lookups
	// across a link) reads its records in that collection like a global one;
	// what differs is copying — allowed only when the link says recipients
	// may keep copies (`allowCopies`). A global source copies as before.
	const linkedSources = useLinkedSources( collectionId, { enabled: foreignTarget } );
	const linkedSource = foreignTarget ? linkedSources.sources.get( targetOwner ) || null : null;
	const sourceIsGlobal = sourceMeta?.publication?.state === 'published' || sourceMeta?.myAccess?.global === true;
	// Not offered until the links are read: a copy the link forbids must never
	// flash up as an option first. And only to a MANAGER of this collection —
	// POST …/copy-references is manage-gated for a global source and a linked
	// one alike, so a writer would be offered a certain 403. (collectionMeta,
	// below, is this collection's own document: its myAccess answers.)
	const [ collectionMeta, setCollectionMeta ] = useState( null );
	const managesHere = canManageCollection( collectionMeta );
	const canCopyHere = foreignTarget && ! familyTarget && managesHere && ! linkedSources.loading && ( ! linkedSource || linkedSource.allowCopies || sourceIsGlobal );
	useEffect( () => {
		if ( !foreignTarget ) { setSourceMeta( null ); return undefined; }
		let live = true;
		api.get( `${API_COLLECTIONS_URL}/${sourceCollectionId}` )
			.then( ( { data } ) => { if ( live ) setSourceMeta( data || null ); } )
			.catch( () => { if ( live ) setSourceMeta( null ); } );
		return () => { live = false; };
	}, [ foreignTarget, sourceCollectionId ] );
	const [ items, setItems ] = useState( [] );
	// What the first page is a page OF (X-Total-Count): the "View all (n)"
	// count is the card's, not the 50 that came back.
	const [ itemsTotal, setItemsTotal ] = useState( 0 );
	const [ loading, setLoading ] = useState( false );
	const [ collectionMetaLoading, setCollectionMetaLoading ] = useState( false );
	const [ detailModalOpen, setDetailModalOpen ] = useState( false );
	const [ detailItemId, setDetailItemId ] = useState( null );
	const [ detailItemData, setDetailItemData ] = useState( null );
	const [ detailLoading, setDetailLoading ] = useState( false );
	const [ detailError, setDetailError ] = useState( null );
	const detailCacheRef = useRef( new Map() );
	const [ viewAllOpen, setViewAllOpen ] = useState( false );
	const [ viewAllPage, setViewAllPage ] = useState( 1 );
	const [ createModalOpen, setCreateModalOpen ] = useState( false );
	const [ createSaving, setCreateSaving ] = useState( false );
	const [ createFormState, setCreateFormState ] = useState( { name: '', elements: {} } );

	// The newest ask owns the list: a global card is known only after the
	// first fetch (from this form's collection), and that answer must not land
	// over the card's own collection's.
	const poolAskRef = useRef( 0 );
	const fetchAvailableItems = useCallback( () => {
		const ask = ++poolAskRef.current;
		if ( !sourceCollectionId || !targetCardId ) {
			setItems( [] );
			setItemsTotal( 0 );
			setLoading( false );
			return;
		}
		setLoading( true );
		// An item of a child card IS an item of its parent — a 'Resident' is a
		// 'Person' — so a lookup pointed at the parent has to offer the whole
		// subtree, not just rows stored under the parent's own card id. The API
		// resolves the descendant set for us (buildItemsQuery in keepr-api).
		// `q_stored`: the filter is stored text the collection runs, so the
		// list reads it as the API's strict gate does (`tags` in collection
		// mode; a removed tag never widens it) — keepr-api utils/lookupFilter.js.
		const filterParam = narrowed ? `&q=${encodeURIComponent( lookupFilter )}&q_stored=true` : '';
		api.get( `${API_ITEMS_URL}?collection_id=${sourceCollectionId}&card_id=${targetCardId}&include_descendants=true${filterParam}` ).then( res => {
			if ( ask !== poolAskRef.current ) return;
			if ( res.status === 200 && Array.isArray( res.data ) ) {
				// Every record the option fetch brings back is readable,
				// whatever the not-readable registry last heard (a link
				// that ended and came back).
				markReadable( res.data.map( row => row?._id ) );
				setItems( res.data );
				setItemsTotal( lookupTotalOf( res.headers, res.data.length ) );
			}
			else {
				setItems( [] );
				setItemsTotal( 0 );
			}
		} ).catch( () => { if ( ask === poolAskRef.current ) { setItems( [] ); setItemsTotal( 0 ); } } ).finally( () => { if ( ask === poolAskRef.current ) setLoading( false ); } );
	}, [sourceCollectionId, targetCardId, narrowed, lookupFilter] );

	useEffect( () => {
		fetchAvailableItems();
	}, [fetchAvailableItems] );

	useEffect( () => {
		if ( !collectionId ) {
			setCollectionMeta( null );
			return;
		}
		let isMounted = true;
		setCollectionMetaLoading( true );
		api.get( `${API_COLLECTIONS_URL}/${collectionId}` ).then( res => {
			if ( !isMounted ) return;
			if ( res.status === 200 ) {
				setCollectionMeta( res.data );
			}
			else {
				setCollectionMeta( null );
			}
		} ).catch( () => {
			if ( isMounted ) setCollectionMeta( null );
		} ).finally( () => {
			if ( isMounted ) setCollectionMetaLoading( false );
		} );
		return () => { isMounted = false; };
	}, [collectionId] );

	useEffect( () => {
		if ( !viewAllOpen ) return;
		setViewAllPage( 1 );
	}, [viewAllOpen] );

	// The target card's hierarchy (parent first) from the app-wide card cache
	// — no per-level GET. The context is optional: this input also renders
	// outside CardDefinitionsProvider (in isolation in tests), where the
	// chain is simply empty and quick create says the definition is
	// unavailable. Option labels need the same cache for nested lookups.
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById;
	const elementSetsById = cardDefinitions?.elementSetsById || null;
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	const targetCardChain = useMemo( () => cardChain( cardsById || {}, targetCardId, elementSetsById ), [cardsById, targetCardId, elementSetsById] );
	// Loading until the cache settles and the target is in it.
	const cardMetaLoading = !!targetCardId && !targetCardChain.length && !!cardDefinitions?.loading;

	const targetCard = useMemo( () => ( targetCardChain[ targetCardChain.length - 1 ] || null ), [targetCardChain] );

	// ---- typing searches the server (KPR-466; utils/lookupSearch.js) ----
	//
	// The first page above is a page: the API's newest 50. Filtering it in
	// the browser hid every record past it — a published Medications
	// collection's 1,102 products answered "lisinopril" with nothing. A typed
	// term is sent to the list endpoint instead, in the same scope (source
	// collection, target card and the cards beneath it, the picker filter
	// when it narrows) and under the caller's own read scope, so a record the
	// reader cannot see is never offered. The first page stays exactly what
	// it was: the list before anything is typed.
	//
	// No name element known (the cards not cached yet, a card with no text
	// element): the browser's filter over the first page, as before — a
	// query naming nothing would match nothing and read as "not here".
	const searchFields = useMemo( () => lookupSearchFields( {
		cardIds: lookupTargetCardIds( cardsById || {}, targetCardId ),
		cardsById: cardsById || {},
		getElementsForCard,
	} ), [ cardsById, targetCardId, getElementsForCard ] );
	const canSearchServer = searchFields.length > 0;
	const searchFilter = narrowed ? lookupFilter : '';
	// Held here, not inside antd, because a pick and a close must clear it
	// and antd tells nobody when it clears its own (rc-select drops the
	// blur and post-select resets on the floor).
	const [ searchText, setSearchText ] = useState( '' );
	const typedTerm = searchText.trim();
	const search = useLookupSearch( {
		collectionId: sourceCollectionId,
		cardId: targetCardId,
		searchText,
		fields: searchFields,
		filter: searchFilter,
		limit: LOOKUP_SEARCH_PAGE_SIZE,
	} );

	// The View all modal: every record, a server page at a time — not the
	// first page sliced in memory, which stopped at 50 — searchable the same
	// way, and opened on what was typed.
	const [ modalSearchText, setModalSearchText ] = useState( '' );
	const modalPage = useLookupSearch( {
		collectionId: sourceCollectionId,
		cardId: targetCardId,
		searchText: canSearchServer ? modalSearchText : '',
		fields: searchFields,
		filter: searchFilter,
		listWhenEmpty: true,
		skip: ( viewAllPage - 1 ) * VIEW_ALL_PAGE_SIZE,
		limit: VIEW_ALL_PAGE_SIZE,
		enabled: viewAllOpen,
	} );
	const openViewAll = useCallback( ( seed = '' ) => {
		setModalSearchText( canSearchServer ? seed : '' );
		setViewAllPage( 1 );
		setViewAllOpen( true );
	}, [ canSearchServer ] );

	// ---- "Me" (utils/lookupMe.js; docs/SCHEMA.md `options.defaultToMe`) ----
	//
	// The signed-in person's OWN record of the target card — the item of the
	// card (or a descendant) whose account link names them — one tap away,
	// because the person logging a visit is usually the staff member who
	// made it. Asked only when the target's family CAN hold somebody's record
	// (an account link on it, an ancestor or a descendant), so a lookup
	// nobody is linked through never costs a request; answered by the API
	// under the caller's own grants (?card_id= narrows it to this card — a
	// Staff lookup is never offered the caller's Resident record). HIDDEN,
	// not disabled, when there is no record: a greyed "Me" would read as
	// "you are not allowed", when the truth is "you are not on this list".
	// Hidden too once the value already holds it — the InputUser "Me" row's
	// rule.
	const meEligible = useMemo(
		() => showMe && !!targetCardId && lookupFamilyHasIdentity( { cardsById, elementSetsById, cardId: targetCardId } ),
		[showMe, targetCardId, cardsById, elementSetsById]
	);
	const { item: meItem } = useMe( sourceCollectionId, { enabled: meEligible, cardId: targetCardId } );
	const meId = normalizeDbId( meItem?._id ) || '';
	// Under a STRICT filter "Me" is offered only when the person's own record
	// is one the filter offers — asked once per record and filter, hidden
	// until the answer is in (a refused save is worse than a late button).
	const [ meOffered, setMeOffered ] = useState( false );
	useEffect( () => {
		if ( ! strictLookup || ! meId ) { setMeOffered( false ); return undefined; }
		let live = true;
		isOffered( { collectionId: sourceCollectionId, cardId: targetCardId, filter: lookupFilter, id: meId } )
			.then( yes => { if ( live ) setMeOffered( yes ); } );
		return () => { live = false; };
	}, [ strictLookup, meId, sourceCollectionId, targetCardId, lookupFilter ] );
	const showMeButton = !!meId && !lookupHoldsMe( value, meId ) && ( ! strictLookup || meOffered );
	// The platform's one merge rule (utils/cards): a child's re-declaration of
	// an inherited name is one input, typed as the child says.
	const mergedElementDefs = useMemo( () => mergeChainElements( targetCardChain ), [targetCardChain] );

	// Quick-create never offers DRIVEN elements: their values are computed by
	// the collection's automations from other items, so an input here would be
	// discarded on save — and a required driven element must not block the
	// create either (the engine fills it in, inline, before the insert).
	// Nor a "Me too" count (KPR-390): a new item starts with nobody's
	// confirmation, and nobody types one.
	const quickCreateElementDefs = useMemo(
		() => mergedElementDefs.filter( def => ! def?.options?.drivenFrom && def?.dataType !== 'confirmations' ),
		[mergedElementDefs]
	);

	// Option labels come from the target card's display template, which may itself
	// nest a card-lookup — pointing at some other card's item, so not something
	// this input's own `items` fetch would ever contain. Without the cache the
	// labels simply fall back to raw ids.

	// Only worth resolving when a lookup element is actually in play — otherwise
	// no label can contain a nested reference, and the whole fetched option list
	// would be scanned for nothing. Descendant rows are checked too: a child
	// card can declare lookups the target's own chain never mentions.
	// Every row on screen — the first page, a search's hits, the modal's
	// page — labels the same way, so all of them seed the nested-lookup pool.
	const optionPool = useMemo( () => {
		const extra = [ ...( search.rows || [] ), ...( modalPage.rows || [] ) ];
		return extra.length ? [ ...( items || [] ), ...extra ] : ( items || [] );
	}, [ items, search.rows, modalPage.rows ] );

	const targetHasLookupElement = useMemo( () => {
		if ( mergedElementDefs.some( def => def?.dataType === LOOKUP_DATA_TYPE ) ) return true;
		if ( typeof getElementsForCard !== 'function' ) return false;
		return optionPool.some( item => ( getElementsForCard( item?.card_id ) || [] )
			.some( def => def?.dataType === LOOKUP_DATA_TYPE ) );
	}, [mergedElementDefs, getElementsForCard, optionPool] );

	const lookupPoolById = useLinkedItems( {
		items: optionPool,
		getElementsForCard,
		enabled: targetHasLookupElement && !!getElementsForCard && !cardDefinitions?.loading,
		resetKey: `${collectionId || ''}:${targetCardId || ''}`,
	} );

	const resolveLookupValue = useMemo( () => (
		getElementsForCard
			? createLookupResolver( { itemsById: lookupPoolById, cardsById, getElementsForCard } )
			: undefined
	), [lookupPoolById, cardsById, getElementsForCard] );

	// Any option row can belong to a descendant of the lookup target, and such a
	// card carries its own display template plus extra elements — so a row is
	// rendered against its OWN card definition. Falls back to the target card
	// when the definition cache is unavailable (this input also renders outside
	// CardDefinitionsProvider) or the item's card is not in it.
	const cardContextForItem = useCallback( ( item ) => {
		const itemCardId = normalizeDbId( item?.card_id );
		const ownCard = ( itemCardId && cardsById ) ? cardsById[ itemCardId ] : null;
		const ownElementDefs = ( itemCardId && getElementsForCard ) ? getElementsForCard( itemCardId ) : null;
		return {
			card: ownCard || targetCard,
			// A child without its own template still inherits the target's:
			// it has every inherited element the template names.
			template: ownCard?.displayTemplate || targetCard?.displayTemplate,
			elementDefs: ownElementDefs?.length ? ownElementDefs : mergedElementDefs,
		};
	}, [cardsById, getElementsForCard, targetCard, mergedElementDefs] );

	const labelForItem = useCallback( ( item ) => {
		const { template, elementDefs } = cardContextForItem( item );
		return getItemDisplayLabel( item, { template, elementDefs, resolveLookupValue } );
	}, [cardContextForItem, resolveLookupValue] );
	const cardSingularLabel = useMemo( () => getCardLabel( targetCard, { plural: false } ), [targetCard] );
	const cardPluralLabel = useMemo( () => getCardLabel( targetCard, { plural: true } ), [targetCard] );

	// What the filter says in the words a list's chips use ("Status is
	// Active"), for the line under the picker — up to two conditions; a longer
	// or nested filter reads as "the ones this field offers".
	// A filter naming tags (by id, as the API stores it) reads with their
	// names: the vocabulary of the collection the picker lists, asked for only
	// when the filter mentions tags (the shared, session-stamped read).
	const filterNamesTags = /\btags\b/.test( lookupFilter );
	const { tags: filterTags, loaded: filterTagsLoaded } = useCollectionTags( sourceCollectionId, { enabled: Boolean( sourceCollectionId ) && filterNamesTags } );
	const filterSentence = useMemo( () => {
		if ( ! lookupFilter || ! targetCardId ) return '';
		const ids = lookupTargetCardIds( cardsById || {}, targetCardId );
		const fields = [
			...buildFilterFields( {
				cards: [ ...ids ].map( id => cardsById?.[ id ] || { _id: id } ),
				cardIds: ids,
				getElementsForCard,
			} ),
			tagsFilterField( { tags: filterTags, mode: 'collection', loaded: filterTagsLoaded } ),
		];
		const { entries, advanced } = summarizeQuery( lookupFilter, fields );
		if ( advanced || ! entries.length || entries.length > 2 ) return '';
		return entries.map( entry => entry.text ).join( ' and ' );
	}, [ lookupFilter, targetCardId, cardsById, getElementsForCard, filterTags, filterTagsLoaded ] );
	const pluralLower = ( cardPluralLabel || 'items' ).toLowerCase();
	const offeredPhrase = filterSentence ? `${pluralLower} where ${filterSentence}` : `the ${pluralLower} this field offers`;

	// What the dropdown lists: the server's hits once a term is being
	// searched (`search.active`), the first page otherwise. A hit carries the
	// term it answered: the server is the authority on it, so it shows even
	// when its label does not spell the term out (a template element matched).
	// Between a keystroke and its answer the previous hits are narrowed here
	// by label — and so is the first page when the server cannot be asked.
	// The filtering is done before the inline cap, not by antd after it,
	// which only ever narrowed the first 10.
	const searching = search.active;
	const selectOptionSource = useMemo( () => ( searching ? search.rows : items || [] )
		.map( item => {
			const id = normalizeDbId( item?._id );
			if ( !id ) return null;
			return {
				value: id,
				label: labelForItem( item ) || id,
				matchedTerm: searching ? search.term : null,
				item,
			};
		} )
		.filter( Boolean )
		.filter( opt => ! typedTerm || opt.matchedTerm === typedTerm || optionSearchFilter( typedTerm, opt ) ), [searching, search.rows, search.term, items, labelForItem, typedTerm] );

	// How many there are in all: the server's count for a search or for the
	// card; only a browser-filtered first page counts what it holds.
	const totalOptionCount = searching
		? Math.max( search.total, selectOptionSource.length )
		: ( typedTerm ? selectOptionSource.length : Math.max( itemsTotal, selectOptionSource.length ) );
	const limitedOptions = useMemo( () => selectOptionSource.slice( 0, MAX_LOOKUP_INLINE_OPTIONS ).map( opt => ( {
		value: opt.value,
		label: opt.label,
	} ) ), [selectOptionSource] );
	const showViewAllOption = totalOptionCount > MAX_LOOKUP_INLINE_OPTIONS;

	const selectOptions = useMemo( () => {
		if ( !showViewAllOption ) return limitedOptions;
		return [
			...limitedOptions,
			{
				value: VIEW_ALL_OPTION_VALUE,
				label: searching
					? `View all ${totalOptionCount} matching “${search.term}”…`
					: `View all ${cardPluralLabel} (${totalOptionCount})…`,
				isViewAll: true,
			},
		];
	}, [limitedOptions, showViewAllOption, cardPluralLabel, totalOptionCount, searching, search.term] );

	const normalizedValue = useMemo( () => {
		if ( allowMultiple ) {
			if ( !value ) return [];
			const arr = Array.isArray( value ) ? value : [ value ];
			return arr
				.map( entry => normalizeDbId( entry ) )
				.filter( Boolean );
		}
		return value ? ( normalizeDbId( value ) || undefined ) : undefined;
	}, [allowMultiple, value] );

	const selectedIds = useMemo( () => {
		if ( allowMultiple ) return normalizedValue || [];
		return normalizedValue ? [ normalizedValue ] : [];
	}, [allowMultiple, normalizedValue] );

	const itemsById = useMemo( () => {
		const map = new Map();
		optionPool.forEach( item => {
			const id = normalizeDbId( item?._id );
			if ( id ) map.set( id, item );
		} );
		return map;
	}, [optionPool] );

	const itemsCount = items?.length || 0;

	// A search that narrows the modal's list can leave it past its last page.
	const modalTotal = modalPage.total;
	useEffect( () => {
		if ( modalPage.loading ) return;
		const totalPages = Math.max( 1, Math.ceil( modalTotal / VIEW_ALL_PAGE_SIZE ) );
		if ( viewAllPage > totalPages ) {
			setViewAllPage( totalPages );
		}
	}, [modalTotal, modalPage.loading, viewAllPage] );

	// A record picked from a search or the modal is remembered: the list it
	// came from moves on (the search clears, the page turns), and the value
	// must keep reading as its title, not its id.
	const rememberPicked = useCallback( ( ids ) => {
		( ids || [] ).forEach( raw => {
			const id = normalizeDbId( raw );
			const row = id ? itemsById.get( id ) : null;
			if ( row && ! detailCacheRef.current.has( id ) ) detailCacheRef.current.set( id, row );
		} );
	}, [itemsById] );

	// A SELECTED id the option fetch did not bring back (outside the page, in
	// another collection than the form's, or no longer readable — a link that
	// ended) is asked for once by id (hooks/useSelectedLookupRows): found, it
	// labels like any other; not, the server lists it in X-Not-Readable and
	// the value reads "No longer available" — never a raw id, which is what a
	// value reads as only until the answer is in (KPR-1233).
	const notReadable = useNotReadableIds();
	const isHeld = useCallback( id => itemsById.has( id ) || detailCacheRef.current.has( id ) || Boolean( lookupPoolById?.[ id ] ), [ itemsById, lookupPoolById ] );
	const { rows: selectedResolved, settled: selectedSettled } = useSelectedLookupRows( { ids: selectedIds, isKnown: isHeld, enabled: ! loading } );
	const heldRow = useCallback( id => detailCacheRef.current.get( id ) || itemsById.get( id ) || lookupPoolById?.[ id ] || selectedResolved[ id ], [ itemsById, lookupPoolById, selectedResolved ] );
	const isUnavailable = useCallback( id => ! heldRow( id ) && ( notReadable.has( id ) || Boolean( selectedSettled[ id ] ) ), [ heldRow, notReadable, selectedSettled ] );

	const optionLabelForId = useCallback( ( id ) => {
		const cached = heldRow( id );
		if ( cached ) return labelForItem( cached ) || id;
		return isUnavailable( id ) ? NOT_READABLE_LABEL : 'Loading…';
	}, [ heldRow, labelForItem, isUnavailable ] );

	// What a selected value reads as in the box: its option's label, else the
	// label resolved above, else — for an id the server says is gone — the
	// muted "No longer available" chip.
	const selectLabelRender = useCallback( ( { value: optionValue, label } ) => {
		const id = normalizeDbId( optionValue );
		if ( label !== undefined && label !== null && label !== optionValue ) return label;
		if ( id && isUnavailable( id ) ) return <LookupUnavailable />;
		return id ? optionLabelForId( id ) : label;
	}, [ isUnavailable, optionLabelForId ] );

	const handleSelectOption = useCallback( ( selectedValue, option ) => {
		if ( selectedValue === VIEW_ALL_OPTION_VALUE || option?.isViewAll ) {
			openViewAll( searching ? search.term : '' );
		}
		// A pick ends the search, in both modes (antd clears its own copy
		// and says nothing).
		setSearchText( '' );
	}, [openViewAll, searching, search.term] );

	const handleChange = useCallback( ( nextValue ) => {
		if ( allowMultiple ) {
			const arr = Array.isArray( nextValue ) ? nextValue : [];
			if ( arr.includes( VIEW_ALL_OPTION_VALUE ) ) {
				openViewAll( searching ? search.term : '' );
			}
			const filtered = arr.filter( val => val && val !== VIEW_ALL_OPTION_VALUE );
			rememberPicked( filtered );
			onChange( elementName, filtered );
		}
		else {
			if ( !nextValue ) {
				onChange( elementName, null );
				return;
			}
			if ( nextValue === VIEW_ALL_OPTION_VALUE ) {
				openViewAll( searching ? search.term : '' );
				return;
			}
			rememberPicked( [ nextValue ] );
			onChange( elementName, nextValue );
		}
	}, [allowMultiple, elementName, onChange, openViewAll, rememberPicked, searching, search.term] );

	// ---- scanning into a lookup ----
	//
	// THIS IS THE HALF THAT CAN CORRUPT DATA, so it resolves through the API
	// rather than through the pool above: that pool is fetched with an inline
	// cap, and a client-side match would silently miss every record past it —
	// "the scanner cannot find the truck" reading identically to "the truck is
	// not in the system".
	//
	// ZERO HITS AND SEVERAL HITS ARE BOTH SHOWN. Picking the first of two
	// matches would be a wrong write that looks like a right one, which is the
	// worst thing a barcode scan can do. Neither case changes the value.
	const scan = scanConfig( elementOptions );
	const [ scanState, setScanState ] = useState( null );

	const handleScan = useCallback( async ( decoded ) => {
		const code = applyStrip( decoded, scan?.strip );
		if ( !code ) return;
		setScanState( { status: 'looking', code } );
		try {
			const scope = { collectionId: sourceCollectionId, cardId: targetCardId, match: scan?.match, value: code };
			// The same pool the dropdown offers: the filter ANDed in.
			const { hits } = await resolveScan( { ...scope, filter: narrowed ? lookupFilter : '' } );
			if ( ! hits.length && narrowed ) {
				// Outside the filter is not "nothing here". Not strict, the
				// record is offered to choose anyway; strict, it is named and
				// cannot be chosen — never "nothing matches".
				const { hits: outside } = await resolveScan( scope );
				if ( outside.length ) {
					setScanState( { status: strictLookup ? 'notOffered' : 'outside', code, hits: outside } );
					return;
				}
			}
			if ( hits.length === 1 ) {
				const id = normalizeDbId( hits[ 0 ]?._id );
				detailCacheRef.current.set( id, hits[ 0 ] );
				if ( allowMultiple ) {
					const current = Array.isArray( value ) ? value.map( normalizeDbId ) : [];
					onChange( elementName, current.includes( id ) ? current : [ ...current, id ] );
				}
				else {
					onChange( elementName, id );
				}
				setScanState( null );
				return;
			}
			setScanState( { status: hits.length ? 'many' : 'none', code, hits } );
		}
		catch ( e ) {
			setScanState( { status: 'failed', code } );
		}
	}, [allowMultiple, sourceCollectionId, elementName, onChange, scan?.match, scan?.strip, targetCardId, value, narrowed, lookupFilter, strictLookup] );

	const chooseScanHit = useCallback( ( item ) => {
		const id = normalizeDbId( item?._id );
		if ( !id ) return;
		detailCacheRef.current.set( id, item );
		if ( allowMultiple ) {
			const current = Array.isArray( value ) ? value.map( normalizeDbId ) : [];
			onChange( elementName, current.includes( id ) ? current : [ ...current, id ] );
		}
		else {
			onChange( elementName, id );
		}
		setScanState( null );
	}, [allowMultiple, elementName, onChange, value] );

	const handleSelectFromModal = useCallback( ( itemId ) => {
		const normalized = normalizeDbId( itemId );
		if ( !normalized ) return;
		rememberPicked( [ normalized ] );
		if ( allowMultiple ) {
			const next = Array.from( new Set( [ ...selectedIds, normalized ] ) );
			onChange( elementName, next );
		}
		else {
			onChange( elementName, normalized );
			setViewAllOpen( false );
		}
	}, [allowMultiple, elementName, onChange, selectedIds, rememberPicked] );

	const openDetailForId = useCallback( ( itemId ) => {
		const normalized = normalizeDbId( itemId );
		if ( !normalized ) return;
		setDetailItemId( normalized );
		setDetailModalOpen( true );
	}, [] );

	const handleViewItemClick = useCallback( () => {
		if ( !selectedIds.length ) return;
		openDetailForId( selectedIds[ 0 ] );
	}, [openDetailForId, selectedIds] );

	useEffect( () => {
		if ( !detailModalOpen || !detailItemId ) {
			setDetailItemData( null );
			setDetailError( null );
			return;
		}
		if ( detailCacheRef.current.has( detailItemId ) ) {
			setDetailItemData( detailCacheRef.current.get( detailItemId ) );
			return;
		}
		let isMounted = true;
		setDetailLoading( true );
		setDetailError( null );
		api.get( `${API_ITEMS_URL}/${detailItemId}` ).then( res => {
			if ( !isMounted ) return;
			if ( res.status === 200 ) {
				markReadable( [ res.data?._id ] );
				detailCacheRef.current.set( detailItemId, res.data );
				setDetailItemData( res.data );
			}
			else {
				setDetailError( 'Unable to load item details.' );
				setDetailItemData( null );
			}
		} ).catch( () => {
			if ( isMounted ) {
				setDetailError( 'Unable to load item details.' );
				setDetailItemData( null );
			}
		} ).finally( () => {
			if ( isMounted ) setDetailLoading( false );
		} );
		return () => { isMounted = false; };
	}, [detailModalOpen, detailItemId] );

	useEffect( () => {
		if ( !detailModalOpen ) return;
		if ( selectedIds.length ) {
			if ( !detailItemId || !selectedIds.includes( detailItemId ) ) {
				setDetailItemId( selectedIds[ 0 ] );
			}
		}
		else {
			setDetailItemId( null );
			setDetailItemData( null );
		}
	}, [detailModalOpen, detailItemId, selectedIds] );

	const handleCloseDetailModal = () => {
		setDetailModalOpen( false );
		setDetailItemId( null );
		setDetailItemData( null );
		setDetailError( null );
	};

	const detailSource = detailItemData || ( detailItemId ? itemsById.get( detailItemId ) : null );
	const detailCollectionId = detailSource?.collection_id || detailSource?.collectionId || collectionId;
	const detailCardContext = useMemo( () => cardContextForItem( detailSource ), [cardContextForItem, detailSource] );
	const detailCardLabel = useMemo( () => getCardLabel( detailCardContext.card, { plural: false } ), [detailCardContext] );
	const detailElementDefs = detailCardContext.elementDefs;

	const handleGoToItem = () => {
		if ( !detailItemId || !detailCollectionId ) return;
		handleCloseDetailModal();
		navigate( ( familyContext && referencedRecordPath( familyContext, detailCollectionId, detailItemId ) )
			|| `/collections/${detailCollectionId}/items/${detailItemId}` );
	};

	const quickCreatePrefix = useMemo( () => `cardLookupCreate_${elementName || 'field'}__`, [elementName] );
	const decorateName = useCallback( ( name ) => quickCreatePrefix + name, [quickCreatePrefix] );
	const stripDecoratedName = useCallback( ( decorated ) => {
		if ( typeof decorated !== 'string' ) return decorated;
		return decorated.startsWith( quickCreatePrefix ) ? decorated.slice( quickCreatePrefix.length ) : decorated;
	}, [quickCreatePrefix] );

	const buildInitialQuickCreateElements = useCallback( () => {
		const defaults = {};
		quickCreateElementDefs.forEach( element => {
			const key = element?.name;
			if ( !key ) return;
			defaults[ key ] = getElementInitialValue( element, { isNewRecord: true, zone: quickCreateZone } );
		} );
		return defaults;
	}, [quickCreateElementDefs, quickCreateZone] );

	useEffect( () => {
		if ( !createModalOpen ) return;
		setCreateFormState( {
			name: '',
			elements: buildInitialQuickCreateElements(),
		} );
	}, [createModalOpen, buildInitialQuickCreateElements] );

	const handleQuickCreateChange = useCallback( ( rawField, nextValue ) => {
		const field = stripDecoratedName( rawField );
		if ( !field ) return;
		if ( field === '__itemName' ) {
			setCreateFormState( prev => ( { ...prev, name: nextValue } ) );
			return;
		}
		setCreateFormState( prev => ( {
			...prev,
			elements: {
				...prev.elements,
				[ field ]: nextValue,
			},
		} ) );
	}, [stripDecoratedName] );

	const renderQuickCreateElement = useCallback( ( element ) => {
		if ( !element?.name ) return null;
		const decoratedName = decorateName( element.name );
		const elementLabel = element?.label?.singular || element.name;
		const options = element?.options || {};
		// A Choice's list only: a short text that carries one (kept from an
		// earlier life as a Choice, KPE-033) is a text box.
		const choices = choiceListOf( element );
		const hasChoices = choices.length > 0;
		const rawValue = createFormState.elements?.[ element.name ];
		const resolvedDefault = getElementInitialValue( element, { isNewRecord: true, zone: quickCreateZone } );
		const commonProps = {
			elementName: decoratedName,
			elementLabel,
			value: rawValue !== undefined ? rawValue : resolvedDefault,
			onChange: handleQuickCreateChange,
			labelBadge: null,
			required: !!options?.required,
		};

		if ( element.dataType === 'choice' && hasChoices && options.allowMultiple === true ) {
			return <InputChoiceMulti key={decoratedName} {...commonProps} options={choices} />;
		}
		if ( element.dataType === 'choice' ) {
			return (
				<InputSelect
					key={decoratedName}
					{...commonProps}
					options={choices}
					selectIcons={!!( options.selectIcons || options.preferChoiceIcons )}
				/>
			);
		}
		if ( element.dataType === 'text-small' ) {
			return <InputText key={decoratedName} {...commonProps} masked={isMaskedElement( element )} disableAutocorrect={!!options?.disableAutocorrect} allowAutocomplete={!!options?.enableAutocomplete} />;
		}
		if ( element.dataType === 'text-large' ) {
			return <InputTextArea key={decoratedName} {...commonProps} masked={isMaskedElement( element )} allowAutocomplete={!!options?.enableAutocomplete} />;
		}
		if ( element.dataType === 'rich-text' ) {
			return <InputRichText key={decoratedName} {...commonProps} />;
		}
		if ( element.dataType === 'date' ) {
			return <InputDate key={decoratedName} {...commonProps} incrementButtons={!!options?.incrementButtons} showRelativeTime={!!options?.showRelativeTime} />;
		}
		if ( element.dataType === 'date-time' ) {
			return <InputDate key={decoratedName} {...commonProps} showTime incrementButtons={!!options?.incrementButtons} showRelativeTime={!!options?.showRelativeTime} />;
		}
		if ( element.dataType === 'time' ) {
			return <InputTime key={decoratedName} {...commonProps} />;
		}
		if ( [ 'number', 'decimal', 'integer' ].includes( element.dataType ) ) {
			return <InputNum key={decoratedName} {...commonProps} masked={isMaskedElement( element )} incrementButtons={!!options?.incrementButtons} dataType={element.dataType} options={options} />;
		}
		if ( element.dataType === 'boolean' ) {
			return <InputBoolean key={decoratedName} {...commonProps} element={element} />;
		}
		if ( element.dataType === 'phone' ) {
			return <InputPhone key={decoratedName} {...commonProps} element={element} />;
		}
		if ( element.dataType === 'email' ) {
			return <InputEmail key={decoratedName} {...commonProps} />;
		}
		if ( element.dataType === 'location' ) {
			return <InputLocation key={decoratedName} {...commonProps} element={element} />;
		}
		if ( element.dataType === 'measurement' ) {
			return <InputMeasurement key={decoratedName} {...commonProps} element={element} cardId={targetCardId} />;
		}
		if ( element.dataType === 'currency' ) {
			return <InputCurrency key={decoratedName} {...commonProps} element={element} />;
		}
		if ( element.dataType === 'rating' ) {
			return <InputRating key={decoratedName} {...commonProps} options={options} />;
		}
		if ( element.dataType === 'color' ) {
			return <InputColor key={decoratedName} {...commonProps} value={commonProps.value ?? ''} presets={COLOR_PRESETS} allowClear showText />;
		}
		if ( element.dataType === 'card-lookup' ) {
			return (
				<InputCardLookup
					key={decoratedName}
					{...commonProps}
					collectionId={collectionId}
					elementOptions={options}
				/>
			);
		}
		return <InputText key={decoratedName} {...commonProps} allowAutocomplete={!!options?.enableAutocomplete} />;
	}, [collectionId, targetCardId, createFormState.elements, decorateName, handleQuickCreateChange, quickCreateZone] );

	const handleCreateSubmit = () => {
		if ( !collectionId || !targetCardId ) {
			message.error( 'Choose a collection and lookup card before creating an item.' );
			return;
		}
		// Never validate against an empty definition: block until the card
		// chain has actually loaded so required fields can't slip through.
		if ( cardMetaLoading ) {
			message.error( 'Card details are still loading. Please try again in a moment.' );
			return;
		}
		if ( !targetCardChain.length ) {
			message.error( 'Card definition unavailable. Reopen this dialog and try again.' );
			return;
		}
		const missingRequired = getMissingRequiredElements( quickCreateElementDefs, createFormState.elements );
		if ( missingRequired.length ) {
			const labels = missingRequired.map( def => def?.label?.singular || def?.name ).filter( Boolean ).join( ', ' );
			message.error( `Please fill in required field${missingRequired.length === 1 ? '' : 's'}: ${labels}` );
			return;
		}
		setCreateSaving( true );
		const payload = {
			collection_id: collectionId,
			card_id: targetCardId,
			name: createFormState.name,
			elements: createFormState.elements,
		};
		api.post( API_ITEMS_URL, payload ).then( async res => {
			if ( res.status !== 200 && res.status !== 201 ) {
				throw new Error( 'Failed to create item' );
			}
			const createdId = normalizeDbId( res.data?._id ) || normalizeDbId( res.data?.id );
			setCreateModalOpen( false );
			fetchAvailableItems();
			// A STRICT filter: the new record is chosen only if the filter
			// offers it — the save would be refused otherwise. It exists either
			// way, and the message says why it was not chosen.
			if ( createdId && strictLookup && ! ( await isOffered( { collectionId: sourceCollectionId, cardId: targetCardId, filter: lookupFilter, id: createdId } ) ) ) {
				message.warning( `${cardSingularLabel} created, but it isn’t one of ${offeredPhrase}, so it wasn’t chosen.` );
				return;
			}
			message.success( `${cardSingularLabel} created.` );
			if ( createdId ) {
				if ( allowMultiple ) {
					const next = Array.from( new Set( [ ...selectedIds, createdId ] ) );
					onChange( elementName, next );
				}
				else {
					onChange( elementName, createdId );
				}
				setDetailItemId( createdId );
			}
		} ).catch( () => {
			message.error( 'Unable to create item right now. Please try again.' );
		} ).finally( () => setCreateSaving( false ) );
	};

	const detailSelectionOptions = selectedIds.map( id => ( {
		value: id,
		label: optionLabelForId( id ),
	} ) );

	const handleMe = useCallback( () => {
		if ( !meId ) return;
		// The record is known before the option list may be: seed the label.
		if ( meItem ) detailCacheRef.current.set( meId, meItem );
		// Single: the record. Multi: APPENDED — "Me" joins a crew, it does
		// not replace it.
		onChange( elementName, withMe( value, meId, { allowMultiple } ) );
	}, [allowMultiple, elementName, meId, meItem, onChange, value] );

	const handleClearSelection = useCallback( () => {
		onChange( elementName, allowMultiple ? [] : null );
	}, [allowMultiple, elementName, onChange] );

	// The field's ⋯ follows the platform action taxonomy: "View record" leads
	// the Go to group (it replaced the eye button that used to sit left of the
	// input), the rest operate on the selection.
	const kebabMenu = useMemo( () => buildActionMenu( [
		{
			key: 'view-record',
			group: 'navigate',
			glyph: 'open',
			label: 'View record',
			disabled: !selectedIds.length,
			onClick: handleViewItemClick,
		},
		{
			key: 'view-all',
			group: 'navigate',
			glyph: 'items',
			label: `View all ${cardPluralLabel}`,
			disabled: !itemsCount,
			onClick: () => openViewAll( '' ),
		},
		// Records of a global collection are read, never added to, from here.
		foreignTarget ? null : {
			key: 'create',
			group: 'record',
			icon: <PLUS />,
			label: `Add ${cardSingularLabel}`,
			disabled: !targetCardId || !collectionId,
			onClick: () => { if ( targetCardId && collectionId ) setCreateModalOpen( true ); },
		},
		// …but a linked one can be COPIED here, to keep it past the source's
		// retirement (plan §4.6) — or past a collection link, when that link
		// lets this collection keep copies.
		canCopyHere ? {
			key: 'copy-here',
			group: 'record',
			glyph: 'duplicate',
			label: 'Copy into this collection…',
			disabled: !selectedIds.length || !collectionId,
			onClick: () => setCopyOpen( true ),
		} : null,
		{
			key: 'clear',
			group: 'record',
			glyph: 'remove',
			label: 'Clear selection',
			disabled: !selectedIds.length,
			onClick: handleClearSelection,
		},
	] ), [cardPluralLabel, cardSingularLabel, collectionId, foreignTarget, canCopyHere, handleClearSelection, handleViewItemClick, itemsCount, openViewAll, selectedIds.length, targetCardId] );

	const isDisabled = disabled || !collectionId || !targetCardId;
	// An empty dropdown under a filter must never read as "no such record":
	// it says what was searched, and — not strict — offers everything.
	const filterNotFound = narrowed ? (
		<div className='card-lookup-filter-note' data-testid='lookup-filter-empty'>
			<span>None of {offeredPhrase} match{ searching && search.term ? <> “{search.term}”</> : null }.</span>
			{ strictLookup ? null : (
				<Button
					type='link'
					size='small'
					className='card-lookup-filter-note__action'
					onMouseDown={event => event.preventDefault()}
					onClick={() => setShowAll( true )}
				>
					Show all
				</Button>
			) }
		</div>
	) : undefined;
	// While a search is out, an empty list is "still looking", and a failed
	// one says so — neither may read as "there is no such record".
	let dropdownEmpty = filterNotFound;
	if ( search.pending && ! selectOptionSource.length ) {
		dropdownEmpty = (
			<div className='card-lookup-search-note' data-testid='lookup-search-pending'>
				<Spin size='small' /> <span>Searching {pluralLower}…</span>
			</div>
		);
	}
	else if ( searching && search.error ) {
		dropdownEmpty = (
			<div className='card-lookup-search-note' data-testid='lookup-search-failed' role='alert'>
				<span>{`Couldn’t search ${pluralLower} right now. Try again.`}</span>
			</div>
		);
	}
	else if ( searching && ! filterNotFound ) {
		dropdownEmpty = (
			<div className='card-lookup-search-note' data-testid='lookup-search-empty'>
				<span>No {pluralLower} match “{search.term}”.</span>
			</div>
		);
	}
	const computedPlaceholder = targetCardId
		? ( placeholder || `Select ${elementLabel}` )
		: 'Configure lookup card type to enable this input';
	// Keep the menu reachable whenever there is a selection so "Clear
	// selection" still works even if the lookup config is broken or empty.
	const kebabDisabled = ( !selectedIds.length ) && ( !itemsCount ) && ( !targetCardId || !collectionId );

	// Hovering the selected value shows the record as its badge. The popover
	// sits on the whole Select rather than on antd's `labelRender` output: in
	// single mode antd lays the search input over the selection item
	// (`.ant-select-selection-search { inset: 0 }`), so a hover on the label
	// itself never fires. Suppressed while the dropdown is open and when
	// nothing is selected.
	const [ dropdownOpen, setDropdownOpen ] = useState( false );
	const [ badgeHover, setBadgeHover ] = useState( false );
	// A record that lives in ANOTHER collection than this form's (a global
	// collection's, a linked one's, a parent's) is shown as its qualified
	// badge — the collection's tile and name before the record
	// (RecordBadge `collection`; the rule reads the surface's collection).
	const recordHomeOf = useCallback( ( item ) => {
		const home = ( item && itemHomeCollectionId( item ) ) || ( foreignTarget ? sourceCollectionId : normalizeDbId( collectionId ) ) || null;
		if ( home && sourceMeta && normalizeDbId( sourceMeta._id ) === home ) return sourceMeta;
		return home;
	}, [ foreignTarget, sourceCollectionId, collectionId, sourceMeta ] );
	const selectedBadges = selectedIds.map( id => {
		const item = detailCacheRef.current.get( id ) || itemsById.get( id ) || lookupPoolById?.[ id ];
		return { id, title: optionLabelForId( id ), card: item ? cardContextForItem( item ).card : targetCard, home: recordHomeOf( item ) };
	} );
	const badgePopoverOpen = badgeHover && !dropdownOpen && selectedBadges.length > 0;

	return (
		<>
			<Wrapper elementName={elementName} elementLabel={elementLabel} labelBadge={labelBadge} required={required} help={help}>
				<Space.Compact className='input-affix-group' block>
					<Popover
						trigger='hover'
						mouseEnterDelay={0.6}
						placement='topLeft'
						open={badgePopoverOpen}
						onOpenChange={setBadgeHover}
						content={(
							<div className='card-lookup-badges' data-testid='lookup-record-badges'>
								{selectedBadges.map( ( { id, title, card, home } ) => (
									<RecordBadge key={id} kind='item' record={card} title={title} size='sm' collection={home} />
								) )}
							</div>
						)}
					>
						<Select
							name={elementName}
							id={elementName}
							size='large'
							className='input-affix-group__input'
							mode={allowMultiple ? 'multiple' : undefined}
							showSearch
							// The options are already the answer to what is typed —
							// the server's hits, or the first page narrowed by LABEL
							// (selectOptionSource) — so antd filters nothing: its
							// default reads `value`, the hex id, and filtering after
							// the inline cap only ever narrowed the first 10. "View
							// all…" stays under any search: it is the door out of the
							// inline cap, not a record.
							filterOption={false}
							// At most ten records and "View all…": nothing to
							// virtualize, and a virtual list would leave the door
							// out of the cap unrendered until scrolled to.
							virtual={false}
							searchValue={searchText}
							onSearch={setSearchText}
							onBlur={() => setSearchText( '' )}
							allowClear
							placeholder={computedPlaceholder}
							disabled={isDisabled}
							loading={loading || search.loading || cardMetaLoading || collectionMetaLoading}
							value={allowMultiple ? normalizedValue : ( normalizedValue ?? undefined )}
							onChange={handleChange}
							onSelect={handleSelectOption}
							onOpenChange={setDropdownOpen}
							options={selectOptions}
							labelRender={selectLabelRender}
							notFoundContent={dropdownEmpty}
						/>
					</Popover>
					{/* An action affix, so it sits right of the input and
					    before the ⋯ (the element-input affix rule). */}
					{ showMeButton ? (
						<Tooltip title={`Your own ${cardSingularLabel.toLowerCase()} record`}>
							<Button
								type='default'
								size='large'
								className='input-affix-group__me'
								disabled={isDisabled}
								onClick={handleMe}
								aria-label={`Fill in my ${cardSingularLabel.toLowerCase()} record`}
								data-testid='lookup-me'
							>
								Me
							</Button>
						</Tooltip>
					) : null }
					{ scan ? (
						<ScanButton
							formats={scan.formats}
							disabled={isDisabled}
							title={`Scan a ${cardSingularLabel.toLowerCase()}`}
							onDecode={handleScan}
						/>
					) : null }
					{/* The ⋯ stays last: it is the catch-all, and every other
					    action sits ahead of it (docs/platform-standards.md). */}
					<Dropdown menu={kebabMenu} trigger={['click']} disabled={kebabDisabled}>
						<Button
							type='default'
							size='large'
							icon={<KEBAB size={16} />}
							aria-label='Lookup actions'
						/>
					</Dropdown>
				</Space.Compact>
				{/* What the list shows, and — not strict — the plain way to
				    everything (utils/lookupFilter.js). */}
				{ lookupFilter && targetCardId ? (
					<div className='card-lookup-filter-note' data-testid='lookup-filter-note'>
						{ strictLookup ? (
							<span>Only {offeredPhrase} can be chosen.</span>
						) : (
							<>
								<span>{ showAll ? `Showing all ${pluralLower}` : `Showing only ${offeredPhrase}` }</span>
								<span aria-hidden='true'>·</span>
								<Button
									type='link'
									size='small'
									className='card-lookup-filter-note__action'
									onClick={() => setShowAll( ! showAll )}
									disabled={isDisabled}
								>
									{ showAll ? 'Show offered only' : 'Show all' }
								</Button>
							</>
						) }
					</div>
				) : null }
			</Wrapper>

			<Modal
				open={!!scanState && scanState.status !== 'looking'}
				onCancel={() => setScanState( null )}
				footer={null}
				title='Scanned code'
				width={520}
				centered
				destroyOnClose
			>
				{ scanState?.status === 'none' ? (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={`Nothing here matches “${scanState.code}”.`}
					/>
				) : null }
				{ scanState?.status === 'failed' ? (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={`“${scanState.code}” could not be looked up. Check the connection and try again.`}
					/>
				) : null }
				{ scanState?.status === 'notOffered' ? (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={`“${scanState.code}” is not one of ${offeredPhrase}, so it can’t be chosen here.`}
					/>
				) : null }
				{ scanState?.status === 'many' || scanState?.status === 'outside' ? (
					<>
						<p className='form-panel__hint'>
							{ scanState.status === 'outside'
								? <>“{scanState.code}” is outside this field’s filter — it is not one of {offeredPhrase}. Choose it anyway if you mean it.</>
								: <>“{scanState.code}” matches {scanState.hits.length} records. Choose the one you mean — nothing is picked for you.</> }
						</p>
						<List
							dataSource={scanState.hits}
							renderItem={item => (
								<List.Item
									key={normalizeDbId( item?._id )}
									actions={[
										<Button key='use' type='primary' size='small' onClick={() => chooseScanHit( item )}>Use</Button>,
									]}
								>
									<RecordBadge
										kind='item'
										record={cardContextForItem( item ).card || targetCard}
										title={labelForItem( item ) || normalizeDbId( item?._id )}
										size='sm'
										collection={recordHomeOf( item )}
									/>
								</List.Item>
							)}
						/>
					</>
				) : null }
			</Modal>

			<Modal
				open={detailModalOpen}
				onCancel={handleCloseDetailModal}
				footer={null}
				title={`${cardSingularLabel} details`}
				width={720}
				centered
				destroyOnClose
			>
				<div className='card-lookup-detail-modal__actions'>
					<Button type='primary' onClick={handleGoToItem} disabled={!detailItemId}>
						Go to Item
					</Button>
					<Dropdown menu={{ items: [ { key: 'coming-soon', label: 'More options coming soon', disabled: true } ] }}>
						<Button
							type='text'
							icon={<KEBAB size={16} />}
							aria-label='More item actions'
						/>
					</Dropdown>
				</div>
				{allowMultiple && selectedIds.length > 1 ? (
					<div style={{ marginBottom: 16 }}>
						<Select
							size='middle'
							value={detailItemId}
							options={detailSelectionOptions}
							onChange={setDetailItemId}
							showSearch
							filterOption={optionSearchFilter}
							style={{ width: '100%' }}
							placeholder='Choose an item to preview'
							aria-label='Item to preview'
						/>
					</div>
				) : null}
				{detailLoading ? (
					<div className='card-lookup-detail-modal__loading'>
						<Spin />
					</div>
				) : detailSource ? (
					<div className='card-lookup-detail-modal__content'>
						<div className='card-lookup-detail-modal__meta'>
							<div><strong>Name:</strong> {labelForItem( detailSource ) || detailSource?.name || detailItemId}</div>
							<div><strong>Item ID:</strong> {detailItemId}</div>
							<div><strong>Card type:</strong> {detailCardLabel}</div>
							<div><strong>Collection:</strong> {collectionMeta?.name || detailCollectionId || '—'}</div>
						</div>
						<div className='card-lookup-detail-modal__elements'>
							{detailElementDefs.length ? detailElementDefs.map( element => {
								const key = element?.name || element?._id;
								const label = element?.label?.singular || element?.name || 'Field';
								const rendered = formatLookupElementValue( element, detailSource?.elements?.[ element?.name ], resolveLookupValue );
								return (
									<div key={key} className='card-lookup-detail-modal__element'>
										<div className='card-lookup-detail-modal__element-label'>{label}</div>
										<div className='card-lookup-detail-modal__element-value'>{rendered}</div>
									</div>
								);
							} ) : <Empty description='No elements configured for this card.' />}
						</div>
					</div>
				) : (
					<Empty description={detailError || 'Select an item to preview.'} />
				)}
			</Modal>

			<Modal
				open={viewAllOpen}
				onCancel={() => setViewAllOpen( false )}
				footer={null}
				title={`All ${cardPluralLabel}`}
				width={720}
				centered
			>
				{ canSearchServer ? (
					<Input
						{...NO_TEXT_ASSIST}
						allowClear
						className='card-lookup-view-modal__search'
						placeholder={`Search ${pluralLower}`}
						aria-label={`Search ${pluralLower}`}
						value={modalSearchText}
						onChange={event => { setModalSearchText( event.target.value ); setViewAllPage( 1 ); }}
						data-testid='lookup-view-all-search'
					/>
				) : null }
				{ modalPage.error ? (
					<Empty description={`Couldn’t load ${pluralLower} right now. Try again.`} />
				) : modalPage.rows.length ? (
					<List
						className='card-lookup-view-modal__list'
						loading={modalPage.loading}
						dataSource={modalPage.rows}
						rowKey={( item, index ) => normalizeDbId( item?._id ) || item?.id || index}
						renderItem={item => {
							const id = normalizeDbId( item?._id );
							const label = labelForItem( item ) || id;
							return (
								<List.Item
									actions={[
										<Button type='link' onClick={() => openDetailForId( id )} key='preview'>Preview</Button>,
										<Button type='primary' onClick={() => handleSelectFromModal( id )} key='select'>Select</Button>,
									]}
								>
									<List.Item.Meta
										title={label || 'Untitled'}
										description={(
											<div className='card-lookup-view-modal__item-meta'>
												<div>ID: {id || '—'}</div>
											</div>
										)}
									/>
								</List.Item>
							);
						}}
					/>
				) : modalPage.loading || ( viewAllOpen && ! modalPage.settled ) ? (
					<div className='card-lookup-detail-modal__loading'>
						<Spin />
					</div>
				) : (
					<Empty description={ modalPage.term ? `No ${pluralLower} match “${modalPage.term}”.` : 'No items yet.' } />
				)}
				{modalTotal > VIEW_ALL_PAGE_SIZE ? (
					<div style={{ marginTop: 16, textAlign: 'right' }}>
						<Pagination
							size='small'
							current={viewAllPage}
							onChange={setViewAllPage}
							total={modalTotal}
							pageSize={VIEW_ALL_PAGE_SIZE}
							showSizeChanger={false}
						/>
					</div>
				) : null}
			</Modal>

			<Modal
				open={createModalOpen}
				onCancel={() => setCreateModalOpen( false )}
				onOk={handleCreateSubmit}
				confirmLoading={createSaving}
				title={`Add ${cardSingularLabel}`}
				width={760}
				destroyOnClose
			>
				{targetCard ? (
					<div className='card-lookup-create-modal'>
						<InputText
							elementName={decorateName( '__itemName' )}
							elementLabel={`${cardSingularLabel} name`}
							value={createFormState.name}
							onChange={handleQuickCreateChange}
							placeholder={`Enter ${cardSingularLabel} name`}
						/>
						<div className='card-lookup-create-modal__fields'>
							{quickCreateElementDefs.length ? quickCreateElementDefs.map( ( element, idx ) => (
								<div key={element?.name || element?._id || idx}>
									{renderQuickCreateElement( element )}
								</div>
							) ) : <Empty description='This card has no elements yet.' />}
						</div>
					</div>
				) : (
					<Empty description='Choose a lookup card to add items.' />
				)}
			</Modal>

			{canCopyHere ? (
				<CopyReferencesModal
					open={copyOpen}
					onClose={() => setCopyOpen( false )}
					collectionId={collectionId}
					variant={linkedSource && ! sourceIsGlobal ? 'linked' : 'global'}
					// The link stopped allowing copies since it was read: read it
					// again, so the action goes away instead of failing twice.
					onLinksStale={linkedSources.refresh}
					source={{ collectionId: sourceCollectionId, name: sourceMeta?.publication?.listing?.title || sourceMeta?.name || linkedSource?.source?.name }}
					itemIds={selectedIds}
					title={selectedIds.length === 1 ? ( selectOptionSource.find( opt => opt.value === selectedIds[ 0 ] )?.label || null ) : null}
					onDone={( report ) => {
						setCopyOpen( false );
						// The form now holds a SOURCE id; the copy replaces it, or
						// the next save would write the original back.
						const copies = report?.copies || {};
						if ( allowMultiple ) {
							onChange( elementName, ( Array.isArray( value ) ? value : [] ).map( id => copies[ normalizeDbId( id ) ] || id ) );
						}
						else if ( value && copies[ normalizeDbId( value ) ] ) {
							onChange( elementName, copies[ normalizeDbId( value ) ] );
						}
					}}
				/>
			) : null}
		</>
	);
}


//////////////////////////////////////////////////
//   
//   RATING
//   
//////////////////////////////////////////////////

export function InputRating ( { elementName, elementLabel, help = '', value, onChange, 
	disabled = false, 
	required = false,
	labelBadge = null,
	// The element's options: `max` is how many stars (2–10, default 5) and
	// `allowHalf` whether a half star can be picked (utils/rating;
	// docs/plans/data-types-second-pass.md §3.1). Absent, five whole stars —
	// what this input always drew.
	options = null,
	} ) {

	const { max, allowHalf } = ratingScale( options );
	const handleChange = ( value ) => {
		onChange( elementName, value );
	};
	
	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			<Rate
				name={elementName}
				id={elementName}
				// id={'input-' + elementName}
				size='large'
				count={max}
				allowHalf={allowHalf}
				value={value}
				onChange={handleChange} 
				disabled={disabled}
				required={required}
				/>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//   
//   COLOR
//   
//////////////////////////////////////////////////

/**
 * A color: antd's ColorPicker, emitting a lower-case "#rrggbb" — the spelling
 * the API stores (utils/elementFormat/color.js, vendored) — or '' when it is
 * cleared. Promoted from the profile's avatar color to the `color` element's
 * input (docs/plans/data-types-second-pass.md §4.1), so one control serves
 * both.
 *
 * The popup is the icon + color modal's palette and full custom panel
 * (honeycomb, sliders, typed Hex / RGB / HSB), not antd's gradient panel
 * (components/common/ColorInputPanel.js; KPR-325). As an element input
 * (`presets`, `allowClear`, `showText`) the trigger also names the color and
 * starts empty instead of white. A stored value the color rule cannot read (a
 * "navy" from before the element was a color) is never handed to the picker,
 * which would read it as something else: the field says what it holds
 * instead, and saving it unchanged is allowed (the echo).
 *
 * antd ≥5.12 passes a CSS string (rgb(...)) as the second onChange argument,
 * not hex; the hex comes from the Color object, always. `disabledAlpha` stays
 * on even for an empty field — antd's cleared color is transparent, which it
 * warns about in development — because without it a color picked from the
 * cleared state keeps alpha 0 and arrives as "#rrggbb00", which the color
 * rule refuses.
 */
export function InputColor ( { elementName, elementLabel, value = '#ffffff', onChange,
	disabled = false,
	required = false,
	help = '',
	labelBadge = null,
	presets = null,
	allowClear = false,
	showText = false,
	status = undefined,
	onBlur = undefined,
} ) {

	const color = swatchColorOf( value );
	const unreadable = typeof value === 'string' && value.trim() && ! color ? value : null;
	const { compact: phone } = useWindowClass();
	const [ open, setOpen ] = useState( false );

	// Only the panel emits now, and always hex; antd's Color object is still
	// accepted for its trigger's own clear block.
	const handleChange = ( picked, css ) => {
		if ( picked?.cleared ) {
			onChange( elementName, '' );
			return;
		}
		const hex = typeof picked === 'string' ? picked : picked?.toHexString ? picked.toHexString() : css;
		const parsed = normalizeColor( hex );
		onChange( elementName, parsed.valid ? parsed.value : hex );
	};

	// Escape and Clear give focus back to the field: the popup is a portal, so
	// without this a keyboard person lands on <body>.
	const closeAndRefocus = () => {
		handleOpenChange( false );
		document.getElementById( elementName )?.focus();
	};

	const handleClear = () => {
		onChange( elementName, '' );
		closeAndRefocus();
	};

	const handleOpenChange = next => {
		setOpen( next );
		if ( ! next && onBlur ) onBlur();
	};

	const handleTriggerKeyDown = event => {
		if ( disabled ) return;
		if ( event.key === 'Enter' || event.key === ' ' ) {
			event.preventDefault();
			handleOpenChange( ! open );
		}
	};

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			<ColorPicker
				name={elementName}
				id={elementName}
				className={presets ? 'input-color' : undefined}
				size='large'
				value={presets ? ( color || '' ) : value}
				open={open}
				onOpenChange={handleOpenChange}
				// Unmounted on close so each open is a fresh panel: Custom closed again,
				// and focus moved in again (ColorInputPanel does that on mount).
				destroyOnHidden
				onChange={handleChange}
				onClear={allowClear ? () => onChange( elementName, '' ) : undefined}
				disabled={disabled}
				required={required}
				disabledAlpha={true}
				disabledFormat={true}
				format='hex'
				allowClear={allowClear}
				showText={showText ? () => ( color || 'No color' ) : undefined}
				// The palette and the full custom panel of the icon + color modal in place
				// of antd's gradient panel (KPR-325). Not the panel inside a popover of
				// our own: that would rebuild antd's trigger, placement and status
				// styling for nothing.
				panelRender={() => (
					<ColorInputPanel
						color={color || ''}
						onPick={hex => handleChange( hex )}
						onClear={allowClear ? handleClear : null}
						onClose={closeAndRefocus}
						phone={phone}
					/>
				)}
				status={status}
				aria-label={elementLabel}
				aria-haspopup='dialog'
				aria-expanded={open}
				role='button'
				tabIndex={disabled ? -1 : 0}
				onKeyDown={handleTriggerKeyDown}
				/>
			{ unreadable ? (
				<div className='input-color__stored'>Holds “{unreadable}”, which is not a color. Pick one to replace it.</div>
			) : null }
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//   
//   ICON PICKER — SELECT-LIKE GRID
//   
//////////////////////////////////////////////////

/**
 * The icon field: a tile-and-label button that opens the icon + color
 * picker (components/common/IconColorPicker.js). Given `onColorChange` (and
 * `colorElementName` / `colorValue`) the one control chooses both halves of
 * a record's identity; without it, the icon alone (the avatar picker).
 * `onChange(elementName, iconKey | null)`; `onColorChange(colorElementName,
 * hex)`. `recordKind` ('card' by default; 'collection', 'elementSet') is the
 * kind of record it dresses, so the tile it previews is the one it will
 * wear — a collection's is the stack.
 */
export function InputIconSelect ( props ) {
	const {
		elementName,
		elementLabel,
		value,
		onChange,
		disabled = false,
		iconCatalog = null,
		iconAliases = undefined,
		colorElementName = 'color',
		colorValue,
		onColorChange = null,
		help = '',
		recordKind = 'card',
	} = props;

	const [ open, setOpen ] = useState( false );
	const withColor = typeof onColorChange === 'function';
	// Only a caller's own catalog can label differently; the library index is the picker's chunk.
	const selectedItem = useMemo( () => ( Array.isArray( iconCatalog ) ? iconCatalog.find( i => i.value === value ) : null ) || null, [ iconCatalog, value ] );
	const label = selectedItem ? selectedItem.label : iconLabelOf( value );
	const prompt = withColor ? 'Choose icon and color' : 'Choose icon';

	return <>
		<Wrapper elementName={elementName} elementLabel={elementLabel} help={help}>
			<div className='icon-field'>
				<button
					type='button'
					id={elementName}
					className='icon-field__trigger'
					disabled={disabled}
					onClick={() => setOpen( true )}
					aria-label={value ? `Selected icon: ${label}. Click to change.` : prompt}
					title={value ? label : prompt}
				>
					<RecordIconTile
						record={{ icon: value || undefined, color: withColor ? colorValue || undefined : undefined, name: '' }}
						kind={recordKind}
						size={40}
						fallbackIcon={null}
					/>
					<span className='icon-field__text'>{value ? label : prompt}</span>
				</button>
				{value ? (
					<Tooltip title='Remove icon'>
						<Button
							type='text'
							icon={<CLOSE size={18} />}
							onClick={() => onChange( elementName, null )}
							aria-label='Remove icon'
							disabled={disabled}
						/>
					</Tooltip>
				) : null}
			</div>
		</Wrapper>
		<IconColorPicker
			open={open}
			onClose={() => setOpen( false )}
			title={elementLabel}
			value={value}
			onChange={val => onChange( elementName, val )}
			color={withColor ? colorValue : undefined}
			onColorChange={withColor ? hex => onColorChange( colorElementName, hex ) : null}
			iconCatalog={iconCatalog}
			iconAliases={iconAliases}
			recordKind={recordKind}
		/>
	</>;
}

// export function InputIconSelect ( props ) {
// 	const {
// 		elementName,
// 		elementLabel,
// 		value,
// 		onChange,
// 		disabled = false,
// 		iconCatalog = null,
// 		iconAliases = {},
// 		pageSize = 25,   // 5x5
// 		gridCols = 5,
// 	} = props;

// 	const [ open, setOpen ] = useState( false );
// 	const [ query, setQuery ] = useState( '' );
// 	const [ page, setPage ] = useState( 1 );

// 	// Build a default catalog from exported icons if none provided
// 	const defaultCatalog = useMemo( () => (
// 		Object.entries( AppIcons )
// 			.filter( ( [ key, Comp ] ) => typeof Comp === 'function' )
// 			.map( ( [ key, Comp ] ) => ( {
// 				value: key,
// 				label: key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
// 				iconJSX: <Comp />,
// 				aliases: iconAliases?.[ key ] || [],
// 				keywords: [],
// 			} ) )
// 	), [ iconAliases ] );

// 	const catalog = Array.isArray( iconCatalog ) && iconCatalog.length ? iconCatalog : defaultCatalog;

// 	// const normalize = ( s ) => ( s || '' ).toString().toLowerCase();
// 	// const matches = ( item, q ) => {
// 	// 	if ( !q ) return true;
// 	// 	const tokens = normalize( q ).split(/\s+/).filter(Boolean);
// 	// 	const hay = [ item.value, item.label, ...( item.aliases || [] ), ...( item.keywords || [] ) ]
// 	// 		.map( normalize )
// 	// 		.join( ' ' );
// 	// 	return tokens.every( t => hay.includes( t ) );
// 	// };

// 	// const filtered = useMemo( () => catalog.filter( i => matches( i, query ) ), [ catalog, query, ] );
// 	const filtered = useMemo( () => {
// 	// Inline the matching logic so the dependency list stays accurate (no function refs)
// 	const q = ( query || '' ).toString().toLowerCase().trim();
// 	if ( !q ) return catalog;
// 	const tokens = q.split( /\s+/ ).filter( Boolean );
// 	return catalog.filter( ( item ) => {
// 		const hay = [ item.value, item.label, ...( item.aliases || [] ), ...( item.keywords || [] ) ]
// 			.map( ( s ) => ( s || '' ).toString().toLowerCase() )
// 			.join( ' ' );
// 		return tokens.every( ( t ) => hay.includes( t ) );
// 	} );
// }, [ catalog, query ] );

// 	useEffect( () => { setPage( 1 ); }, [ query ] );

// 	const pageStart = ( page - 1 ) * pageSize;
// 	const pageEnd = pageStart + pageSize;
// 	const pageItems = filtered.slice( pageStart, pageEnd );

// 	const handlePick = ( val ) => {
// 		onChange( elementName, val );
// 		setOpen( false );
// 	};

// 	const CurrentIcon = () => {
// 		const found = catalog.find( i => i.value === value );
// 		return found ? <span className='d-inline-flex align-items-center'>{found.iconJSX}<span style={{marginLeft:'.5em'}}>{found.label}</span></span> : <em>None</em>;
// 	};

// 	const Panel = (
// 		<div style={{ width: 420 }}>
// 			<div style={{ marginBottom: '.5rem' }}>
// 				<Input
// 					placeholder='Search icons… (name or alias)'
// 					allowClear
// 					value={query}
// 					// onChange={e => setQuery( e.target.value )}
// 					onChange={( e ) => { setQuery( e.target.value ); setPage( 1 ); }}
// 				/>
// 			</div>

// 			<div
// 				style={{
// 					display: 'grid',
// 					gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
// 					gap: '.5rem',
// 					maxHeight: 320,
// 					overflowY: 'auto',
// 					padding: '.25rem',
// 				}}
// 			>
// 				{pageItems.map( item => (
// 					<Button
// 						key={item.value}
// 						type={value === item.value ? 'primary' : 'default'}
// 						onClick={() => handlePick( item.value )}
// 						style={{ height: 56, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
// 					>
// 						<span style={{ display:'inline-flex', alignItems:'center' }}>
// 							<span style={{ marginRight: '.35rem' }}>{item.iconJSX}</span>
// 							<span style={{ fontSize: 12 }}>{item.label}</span>
// 						</span>
// 					</Button>
// 				))}
// 				{pageItems.length === 0 ? <div className='text-muted' style={{ gridColumn: `1 / span ${gridCols}`, padding: '0.5rem' }}>No icons match your search.</div> : null}
// 			</div>

// 			{filtered.length > pageSize ? (
// 				<div style={{ marginTop: '.5rem', display: 'flex', justifyContent: 'flex-end' }}>
// 					<Pagination
// 						size='small'
// 						current={page}
// 						pageSize={pageSize}
// 						total={filtered.length}
// 						showSizeChanger={false}
// 						onChange={(p) => setPage(p)}
// 					/>
// 				</div>
// 			) : null}
// 		</div>
// 	);

// 	return <>
// 		<Wrapper elementName={elementName} elementLabel={elementLabel}>
// 			<div className='d-flex align-items-center' style={{ gap: '.5rem' }}>
// 				<Popover
// 					open={open}
// 					onOpenChange={(v) => setOpen(v)}
// 					trigger='click'
// 					placement='bottomLeft'
// 					content={Panel}
// 				>
// 					<Button
// 						disabled={disabled}
// 						// onClick={() => setOpen(true)}
// 						onClickCapture={(e) => { e.stopPropagation(); setOpen( true ); }}
// 					>
// 						{value ? 'Change' : 'Choose'} Icon
// 					</Button>
// 				</Popover>
// 				<div style={{flex:1}}><CurrentIcon /></div>
// 				{value ? <Button type='text' danger onClick={() => onChange( elementName, null )}>Clear</Button> : null}
// 			</div>
// 		</Wrapper>
// 	</>;
// }



//////////////////////////////////////////////////
//   
//   ICON PICKER
//   
//////////////////////////////////////////////////

// InputIcon (the pre-2025 search-only picker) was deleted 2026-09-21: it had no
// importer left, and InputIconSelect above is the picker.

//////////////////////////////////////////////////
//   
//   DATE
//   
//////////////////////////////////////////////////

/**
 * `date` and `date-time` entry. Everything lives in `common/DateField`; this
 * keeps the props, the option keys and the element-form call sites exactly as
 * they were. `showTime` picks the mode, as it always did.
 *
 * The one contract change is the value it COMMITS: a `date` now yields the
 * calendar day as 'YYYY-MM-DD' instead of a timestamp the API would slice a
 * UTC day off (a day early for anyone east of UTC). `date-time` still commits
 * a full ISO instant.
 */
export function InputDate ( { elementName, elementLabel, help = '', value, onChange,
	disabled = false,
	required = false,
	// eslint-disable-next-line no-unused-vars
	defaultToday = true, // legacy prop: the card definition owns defaults now
	showTime = false, // include time in the field (date-time)
	incrementButtons = false, // per-element option: suffix step buttons
	showRelativeTime = false, // per-element option: "3 days ago" suffix inside the input
	// The element's Earliest / Latest, already resolved against this row's
	// other values (utils/dateBounds), and its minute step.
	min = null,
	max = null,
	minuteStep = undefined,
	// The `date` element's second-pass options (utils/dateOptions): which days
	// of the week it may land on, and how coarsely it is read and entered.
	weekdays = null,
	precision = 'day',
	status = undefined,
	labelBadge = null,
} ) {

	return <DateField
		mode={showTime ? 'date-time' : 'date'}
		elementName={elementName}
		elementLabel={elementLabel}
		value={value}
		onChange={onChange}
		disabled={disabled}
		required={required}
		incrementButtons={incrementButtons}
		showRelativeTime={showRelativeTime}
		min={min}
		max={max}
		minuteStep={minuteStep}
		weekdays={weekdays}
		precision={precision}
		status={status}
		wrapper={Wrapper}
		wrapperProps={{ elementName, elementLabel, labelBadge, required, help }}
	/>;

}



//////////////////////////////////////////////////
//
//   TIME
//
//////////////////////////////////////////////////

/**
 * `time` entry — the same field in its clock mode. It commits **'HH:mm'**,
 * the shape the API's own paths have always written; the old control stored a
 * full ISO timestamp, which is why a time used to render as a raw ISO string
 * in a list (there was no `time` case to read it).
 */
export function InputTime ( { elementName, elementLabel, help = '', value, onChange,
	disabled = false,
	required = false,
	min = null,
	max = null,
	minuteStep = undefined,
	status = undefined,
	labelBadge = null,
} ) {

	return <DateField
		mode='time'
		elementName={elementName}
		elementLabel={elementLabel}
		value={value}
		onChange={onChange}
		disabled={disabled}
		required={required}
		min={min}
		max={max}
		minuteStep={minuteStep}
		status={status}
		wrapper={Wrapper}
		wrapperProps={{ elementName, elementLabel, labelBadge, required, help }}
	/>;

}



//////////////////////////////////////////////////
//
//   USER
//
//////////////////////////////////////////////////

/**
 * A `user` element: a reference to a keepr account, picked from the
 * collection's collaborators (or anyone, by full email address) through
 * UserPickerSelect — docs/plans/element-sets-and-me.md. With
 * `options.allowMultiple` the value is a LIST of ids (kept sorted, the
 * API's own spelling); otherwise one id, '' when nobody is picked.
 *
 * "Me" is the first row of the picker whenever the caller is not already
 * the value: an assignee is oneself often enough that it must not be a
 * search. The row picks the caller's ID — a stored value, not the keyword
 * (that is the filter builder's "Me"; see UserPickerSelect meMode).
 *
 * `status` / `onBlur` follow the other typed inputs: a message the API
 * answered about this field (400 `user_not_found`, 409
 * `account_already_linked`) lands under it through EventFormElement.
 */
export function InputUser ( { elementName, elementLabel, help = '', value, onChange,
	element = null, collectionId, disabled = false, labelBadge = null, required = false,
	placeholder = null, status = undefined, onBlur = undefined, showMe = true } ) {

	const multiple = !! element?.options?.allowMultiple;

	const handleChange = next => onChange( elementName, normalizeUserValue( next, element ) );

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required}
			help={help}>
			<UserPickerSelect
				id={elementName}
				className='w-100'
				size='large'
				collectionId={collectionId}
				multiple={multiple}
				meMode={showMe ? 'shortcut' : null}
				value={multiple ? ( Array.isArray( value ) ? value : ( value ? [ value ] : [] ) ) : ( value || undefined )}
				onChange={handleChange}
				disabled={disabled}
				placeholder={placeholder || ( multiple ? 'Pick people, or type a full email address' : 'Pick a person, or type a full email address' )}
				aria-label={elementLabel}
				status={status}
				onBlur={onBlur}
			/>
		</Wrapper>
	</>;

}



//////////////////////////////////////////////////
//   
//   FILE
//   
//////////////////////////////////////////////////

/**
 * A file element (PR 10b): components/common/FileUploadField — the held-file
 * upload, its thumbnails and "Removed when you save". With the collection's
 * attachments OFF the element takes nothing (the server refuses a new file
 * with `attachments_disabled`) and says so in its place; it is not asked for
 * as required, and a stored value rides along unchanged (the echo).
 *
 * As a FILE COLUMN of a repeating group (KPR-934) it is the same input: `group`
 * and `entryKey` say which entry's column this is, and `item` is the item whose
 * `files` facts describe the stored files.
 */
export function InputFile ( { elementName, elementLabel, help = '', value, onChange,
	element = null, collectionId, cardId, item = null, storedValue = undefined,
	attachmentsOff = false, disabled = false, labelBadge = null, required = false,
	group = null, entryKey = null } ) {

	return <>
		<Wrapper
			elementName={elementName}
			elementLabel={elementLabel}
			labelBadge={labelBadge}
			required={required && ! attachmentsOff}
			help={help}>
			{ attachmentsOff ? (
				<div className='file-upload__off' data-testid='file-upload-off'>Files are off in this collection.</div>
			) : (
				<FileUploadField
					id={elementName}
					element={element}
					value={value}
					onChange={onChange}
					collectionId={collectionId}
					cardId={cardId}
					item={item}
					storedValue={storedValue}
					disabled={disabled}
					group={group}
					entryKey={entryKey}
				/>
			) }
		</Wrapper>
	</>;

}
